import React, { createContext, useContext, useState, useEffect } from "react";

const translations = {
  en: {
    // Common
    dashboard: "Dashboard",
    transactions: "Transactions",
    deposits: "Deposits",
    withdrawals: "Withdrawals",
    merchants: "Merchants",
    providers: "Providers",
    routing: "Routing",
    webhooks: "Webhooks",
    settlements: "Settlements",
    settings: "Settings",
    engines: "Engines",
    resellers: "Resellers",
    servers: "Servers",
    allTransactions: "All Transactions",
    staff: "Staff",
    staffMembers: "Staff Members",
    staffMembersCount: "staff members",
    staffMember: "Staff Member",
    createStaffMember: "Create Staff Member",
    noStaffMembersFound: "No staff members found",
    noRolesAssigned: "No roles assigned",
    changeStaffStatus: "Change Staff Status",
    deleteStaffMember: "Delete Staff Member",
    areYouSureYouWantTo: "Are you sure you want to",
    actions: "Actions",
    roles: "Roles",
    description: "Description",
    analytics: "Analytics",
    searchByIdOrReference: "Search by ID or reference...",
    loginLogs: "Login Logs",
    apiUsageLogs: "API Usage Logs",


    // Actions
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    add: "Add",
    create: "Create",
    update: "Update",
    view: "View",
    search: "Search",
    filter: "Filter",
    export: "Export",
    import: "Import",
    refresh: "Refresh",
    reset: "Reset",
    submit: "Submit",
    confirm: "Confirm",
    close: "Close",
    back: "Back",
    next: "Next",
    previous: "Previous",
    loading: "Loading...",
    saving: "Saving...",
    creating: "Creating...",
    processing: "Processing...",
    postMethod: "POST",
    getMethod: "GET",

    // Status
    active: "Active",
    inactive: "Inactive",
    pending: "Pending",
    success: "Success",
    failed: "Failed",
    declined: "Declined",
    expired: "Expired",
    unknown: "Unknown",
    cancelled: "Cancelled",
    suspended: "Suspended",
    terminated: "Terminated",
    completed: "Completed",
    maintenance: "Maintenance",
    degraded: "Degraded",
    ewallet: "E-Wallet",
    card: "Card",
    qr: "QR",
    bank: "Bank",

    // Time
    today: "Today",
    thisDate: "Today",
    thisWeek: "This Week",
    thisMonth: "This Month",
    thisYear: "This Year",
    customRange: "Custom Range",
    selectRange: "Select Range",

    // Navigation
    logout: "Logout",
    profile: "Profile",
    notifications: "Notifications",
    markAllRead: "Mark all read",
    newNotifications: "New",
    noUnreadNotifications: "No unread notifications",
    viewAllNotifications: "View all notifications",
    notificationMarkedAsRead: "Notification Marked as Read",
    notificationRemoved: "Notification Removed",
    allNotificationsMarkedRead: "All Notifications Marked as Read",
    failedToLoadNotifications: "Failed to Load Notifications",
    errorMarkingAsRead: "Failed to Mark as Read",
    errorRemovingNotification: "Failed to Remove Notification",
    allNotificationsAlreadyRead: "All Notifications Already Read",
    someNotificationsFailedToMarkRead: "Some Notifications Could Not Be Marked as Read",
    failedToMarkAsRead: "Failed to Mark as Read",
    noNotificationsToClear: "No Notifications to Clear",
    allNotificationsCleared: "All Notifications Cleared",
    someNotificationsCouldNotBeRemoved: "Some Notifications Could Not Be Removed",
    failedToRemoveNotification: "Failed to Remove Notification",
    allNotifications: "View all your notifications including withdrawals and deposits",
    lastUpdated: "Last Updated",
    notUpdatedYet: "Not Updated Yet",
    searchForNotifications: "Search notifications...",
    noNotifications: "No Notifications",
    successful: "Successful",
    systemNotification: "System",
    all: "All",
    read: "Read",
    unread: "Unread",
    newWithdrawalNotification: "New Withdrawal",
    newDepositNotification: "New Deposit",
    withdrawalSuccess: "Withdrawal Success",
    depositSuccess: "Deposit Success",
    withdrawalRequested: "Withdrawal Requested",
    welcome: "Welcome",
    requestedWithdrawal: "requested a withdrawal of",
    receivedDeposit: "received a deposit of",
    yourWithdrawalRequestOf: "Your withdrawal request of",
    hasBeenReceivedPendingReview: "has been received and is pending review.",

    // Dashboard
    paymentOrchestration: "Payment orchestration overview",
    balance: "Balance",
    adminBalance: "Admin Balance",
    totalDeposits: "Total Deposits",
    totalWithdrawals: "Total Withdrawals",
    successRate: "Success Rate",
    activeMerchants: "Active Merchants",
    quickActions: "Quick Actions",
    addMerchant: "Add Merchant",
    onboardNewMerchant: "Onboard new merchant",
    configureProvider: "Configure Provider",
    addPaymentProvider: "Add payment provider",
    routingRules: "Routing Rules",
    configureRouting: "Configure routing",
    pendingTransactions: "Pending Transactions",
    requiresAttention: "Requires attention",
    volumeOverview: "Volume Overview",
    last7Days: "Last 7 days transaction volume",
    recentTransactions: "Recent Transactions",
    viewAll: "View All",
    viewMore: "View More",
    totalVolume: "Total Volume",
    volumeOverviewTitle: "Volume Overview",
    transactionVolume: "Transaction Volume",
    allStatus: "All Status",
    allCurrencies: "All Currencies",

    // Transactions
    transactionsFound: "transactions found",
    transactionId: "Transaction ID",
    merchantRef: "Merchant Ref",
    type: "Type",
    amount: "Amount",
    status: "Status",
    date: "Date",
    provider: "Provider",
    deposit: "Deposit",
    withdrawal: "Withdrawal",
    showing: "Showing",
    to: "to",
    of: "of",
    page: "Page",

    // Merchants
    merchantsRegistered: "merchants registered",
    searchMerchants: "Search merchants...",
    searchMerchantsByIdEmailName: "Search by ID, email, or name...",
    merchant: "Merchant",
    code: "Code",
    parent: "Parent",
    features: "Features",
    fee: "Fee",
    settlement: "Settlement",
    direct: "Direct",
    viewDetails: "View Details",
    viewApiKeys: "View API Keys",
    suspend: "Suspend",
    activate: "Activate",
    deleteMerchant: "Delete Merchant",
    deleteConfirmation: "Are you sure you want to delete",
    cannotBeUndone: "This action cannot be undone.",
    suspendMerchantTitle: "Suspend Merchant",
    activateMerchantTitle: "Activate Merchant",
    suspendMerchantConfirm: "Are you sure you want to suspend",
    activateMerchantConfirm: "Are you sure you want to activate",
    willNotProcessTransactions: "They will not be able to process transactions.",
    noMerchantsFound: "No merchants found",

    // Providers
    paymentProviders: "Payment Providers",
    activeProviders: "active providers",
    searchProviders: "Search providers...",
    addProvider: "Add Provider",
    healthScore: "Health Score",
    response: "Response",
    priority: "Priority",
    disable: "Disable",
    enable: "Enable",
    deleteProvider: "Delete Provider",
    affectRouting: "This will affect routing rules.",

    // Routing
    routingRulesTitle: "Routing Rules",
    configureProviderSelection: "Configure provider selection and failover logic",
    addRule: "Add Rule",
    howRoutingWorks: "How Routing Works",
    routingDescription: "Rules are evaluated in priority order. The first matching rule determines which providers handle the transaction.",
    noRoutingRules: "No routing rules configured",
    viewTransactionRoutingConfig: "View transaction routing configuration",
    failover: "Failover",
    amountRange: "Amount Range",
    createFirstRule: "Create your first routing rule to start directing transactions",
    defaultRule: "Default Rule",
    ruleName: "Rule Name",
    defaultQrRoutePlaceholder: "e.g., Default QR Route",
    transactionType: "Transaction Type",
    paymentMethod: "Payment Method",
    minAmount: "Min Amount",
    maxAmount: "Max Amount",
    specificMerchant: "Specific Merchant (optional)",
    allMerchants: "All Merchants",
    routingMode: "Routing Mode",
    providerSequence: "Provider Sequence (click to add/remove)",
    clickProvidersOrder: "Click providers in the order you want them used. Numbers show priority.",
    enableFailover: "Enable Failover",
    autoTryNextProvider: "Automatically try next provider on failure",
    maxFailoverAttempts: "Max Failover Attempts",
    allTypes: "All Types",
    depositsOnly: "Deposits Only",
    withdrawalsOnly: "Withdrawals Only",
    allMethods: "All Methods",
    priorityMode: "Priority (use first available)",
    roundRobin: "Round Robin (distribute evenly)",
    weighted: "Weighted (by provider weight)",
    leastLoad: "Least Load (by current volume)",
    editRoutingRule: "Edit Routing Rule",
    createRoutingRule: "Create Routing Rule",
    deleteRoutingRule: "Delete Routing Rule",
    affectTransactionRouting: "This may affect transaction routing.",

    // Webhook Logs
    webhookLogs: "Webhook Logs",
    webhookEvents: "webhook events",
    webhookId: "Webhook ID",
    direction: "Direction",
    source: "Source",
    event: "Event",
    inbound: "Inbound",
    outbound: "Outbound",
    allDirection: "All Direction",
    received: "Received",
    validated: "Validated",
    processed: "Processed",
    invalidSignature: "Invalid Signature",
    invalid_signature: "Invalid Signature",
    paymentFailed: "Payment Failed",
    paymentSucceeded: "Payment Succeeded",
    withdrawalSucceeded: "Withdrawal Succeeded",
    withdrawalFailed: "Withdrawal Failed",
    webhookLog: "Webhook Log",
    webhookDetails: "Webhook Details",
    requestHeaders: "Request Headers",
    requestPayload: "Request Payload",
    responseStatus: "Response",
    noWebhookLogs: "No webhook logs found",
    duplicate: "Duplicate",
    failedToLoadWebhookLogs: "Failed to load webhook logs",
    noResponseBody: "No response body",

    // Settlements
    settlementTitle: "Settlements",
    merchantPayoutManagement: "Merchant payout management",
    createSettlement: "Create Settlement",
    searchSettlements: "Search settlements...",
    settlementId: "Settlement ID",
    period: "Period",
    netAmount: "Net Amount",
    systemCreatedAt: "Sys Created",
    lastModified: "Last Modified",
    process: "Process",
    markCompleted: "Mark Completed",
    exportReport: "Export Report",
    periodStart: "Period Start",
    periodEnd: "Period End",
    settlementTotalDeposits: "Total Deposits",
    settlementTotalWithdrawals: "Total Withdrawals",
    totalFees: "Total Fees",
    totalSettled: "Total Settled",
    noSettlements: "No settlements found",

    // Settings
    settingsTitle: "Settings",
    platformConfig: "Platform configuration and preferences",
    saveChanges: "Save Changes",
    general: "General",
    security: "Security",
    callbacks: "Callbacks",
    qrSettings: "QR Settings",
    notificationsSettings: "Notifications",
    generalSettings: "General Settings",
    basicPlatformConfig: "Basic platform configuration",
    platformName: "Platform Name",
    timezone: "Timezone",
    defaultCurrency: "Default Currency",
    securitySettings: "Security Settings",
    apiSecurityConfig: "API security and authentication configuration",
    requireIpWhitelist: "Require IP Whitelist",
    acceptApiCallsFromWhitelistedIps: "Only accept API calls from whitelisted IPs",
    hmacAlgorithm: "HMAC Algorithm",
    signatureHeaderName: "Signature Header Name",
    timestampTolerance: "Timestamp Tolerance (seconds)",
    rejectOldRequests: "Reject requests with timestamps older than this value",
    callbackSettings: "Callback Settings",
    configureCallback: "Configure merchant callback behavior",
    enableCallbackRetry: "Enable Callback Retry",
    autoRetryFailedCallbacks: "Automatically retry failed callbacks",
    maxRetryAttempts: "Max Retry Attempts",
    retryDelaySchedule: "Retry Delay Schedule (seconds)",
    callbackTimeout: "Callback Timeout (seconds)",
    qrPaymentSettings: "QR Payment Settings",
    promptPayConfig: "PromptPay QR code configuration",
    defaultQrExpiry: "Default QR Expiry (minutes)",
    minimumAmount: "Minimum Amount (PKR)",
    maximumAmount: "Maximum Amount (PKR)",
    supportedBanks: "Supported Banks",
    enableInProviderSettings: "Enable in Provider settings",
    notificationSettings: "Notification Settings",
    configureAlerts: "Configure alerts and notifications",
    emailNotifications: "Email Notifications",
    sendAlertsViaEmail: "Send alerts via email",
    slackNotifications: "Slack Notifications",
    sendAlertsToSlack: "Send alerts to Slack channel",
    alertThreshold: "Alert Threshold (PKR)",
    sendAlertAboveAmount: "Send alert for transactions above this amount",
    alertTypes: "Alert Types",
    highValueTransactions: "High-value transactions",
    providerFailures: "Provider failures / degradation",
    failedCallbackRetries: "Failed callback retries",
    suspiciousActivity: "Suspicious activity detection",
    settingRetryDelayScheduleNote: "Comma-separated delay values: 30, 60, 300, 600, 1800",
    loadingSettings: "Loading platform settings...",
    failedToLoadSettings: "Failed to load settings. Please try again later.",
    failedToSaveSettings: "Failed to save settings. Please try again later.",
    settingsSavedSuccessfully: "Platform settings updated successfully.",
    tryAgain: "Try Again",
    error: "Error",

    // Engine Settings
    feesAndCommission: "Fees & Commission",
    feesAndCommissionDesc: "Commission rates and fee information",
    givenBy: "Given by {type}: {name}",
    yourCommissionRates: "Your Commission Rates",
    yourCommissionRatesDesc: "These are the rates assigned to you",
    depositCommission: "Deposit Commission",
    withdrawalCommission: "Withdrawal Commission",
    percentageFee: "Percentage Fee",
    depositCommissionPercent: "Deposit Commission (%)",
    withdrawalCommissionPercent: "Withdrawal Commission (%)",
    resellerProfile: "Reseller Profile",
    manageProfile: "Manage your profile settings",
    apiCredentialsTitle: "API Credentials",
    yourApiKeysIntegration: "Your API keys for integration",
    regenerateApiKeysTitle: "Regenerate Keys",
    regenerateApiKeysDesc: "This will invalidate your current API keys immediately.",
    regenerateApiKeysConfirm: "Are you sure you want to regenerate your API keys? This action cannot be undone.",

    // Engines
    enginePartners: "engines registered",
    searchEngines: "Search engines...",
    createEngine: "Create Engine",
    engine: "Engine",
    volume: "Volume",
    created: "Created",
    suspendEngine: "Suspend Engine",
    activateEngine: "Activate Engine",
    allResellersAffected: "All resellers under them will be affected.",
    deleteEngine: "Delete Engine",
    editEngine: "Edit Engine",
    createNewEngine: "Create New Engine",
    updateEngineDetails: "Update engine details",
    setUpNewEnginePartner: "Set up a new engine partner",
    generalTab: "General",
    feesLimitsTab: "Fees & Limits",
    permissionsTab: "Permissions",
    apiAccessTab: "API Access",
    generalInformation: "General Information",
    businessNameRequired: "Business Name *",
    engineCode: "Engine Code",
    loginEmailRequired: "Login Email *",
    newPasswordOptional: "New Password (optional)",
    passwordRequired: "Password *",
    leaveBlankToKeepCurrent: "Leave blank to keep current",
    enterPassword: "Enter password",
    contactName: "Contact Name",
    contactPhone: "Contact Phone",
    supportEmail: "Support Email",
    notes: "Notes",
    configureFeeStructureLimits: "Configure fee structure and operational limits",
    depositFeeThb: "Deposit Fee (RS)",
    withdrawalFeeThb: "Withdrawal Fee (RS)",
    percentageFeePercent: "Percentage Fee (%)",
    commissionRatePercent: "Commission Rate (%)",
    maxResellers: "Max Resellers",
    maxMerchants: "Max Merchants",
    allowedPaymentProviders: "Allowed Payment Providers",
    controlEngineActions: "Control what actions this engine can perform",
    canCreateResellers: "Can Create Resellers",
    allowCreatingNewResellerAccounts: "Allow creating new reseller accounts",
    canCreateMerchants: "Can Create Merchants",
    canCreateMerchantsDirect: "Can Create Merchants (Direct)",
    allowCreatingMerchantAccountsDirectly: "Allow creating merchant accounts directly",
    canEditFeeStructure: "Can Edit Fee Structure",
    allowModifyingFeeSettings: "Allow modifying fee settings",
    canViewWebhookLogs: "Can View Webhook Logs",
    accessToRawWebhookData: "Access to raw webhook data",
    canRegenerateApiKeys: "Can Regenerate API Keys",
    allowRegeneratingApiCredentials: "Allow regenerating API credentials",
    apiCredentials: "API Credentials",
    shareCredentialsSecurely: "Share these credentials securely with the engine partner",
    keepCredentialsSecure: "Keep these credentials secure. They provide full API access.",
    updateEngine: "Update Engine",
    pleaseFillAllRequiredFields: "Please fill in all required fields",
    engineCodePlaceholder: "ENG001",
    businessNamePlaceholder: "Gaming Partners Ltd",
    loginEmailPlaceholder: "admin@engine.com",

    // Resellers
    recent20Transactions: "Recent 20 Transactions",

    resellerPartners: "resellers registered",
    searchResellers: "Search resellers...",
    createReseller: "Create Reseller",
    reseller: "Reseller",
    monthlyVolume: "Monthly Volume",
    suspendReseller: "Suspend Reseller",
    activateReseller: "Activate Reseller",
    merchantsWillBeAffected: "They and their merchants will not be able to process transactions.",
    deleteReseller: "Delete Reseller",
    merchantsBecomeOrphaned: "All merchants under this reseller will become orphaned.",
    merchantsUnderThisReseller: "Merchants Under This Reseller",
    editReseller: "Edit Reseller",
    createNewReseller: "Create New Reseller",
    updateResellerDetails: "Update reseller details and permissions",
    setUpNewResellerPartner: "Set up a new reseller partner",
    resellerProfileAndContact: "Reseller profile and contact details",
    resellerCode: "Reseller Code",
    parentType: "Parent Type",
    whoManagesThisReseller: "Who manages this reseller",
    emailCannotBeChangedAfterCreation: "Email cannot be changed after creation",
    newPasswordLeaveBlank: "New Password (leave blank to keep current)",
    configureResellerFeeStructure: "Configure reseller fee structure and limits",
    maxMerchantsLabel: "Max Merchants",
    maximumMerchantsResellerCanCreate: "Maximum merchants this reseller can create",
    resellerCommissionOnTransactions: "Reseller's commission on transactions",
    thisCommissionRateSetBy: "This commission rate was set by {name} and cannot be changed.",
    allowedProviders: "Allowed Providers",
    selectWhichPaymentProviders: "Select which payment providers this reseller can use",
    routingOverride: "Routing Override",
    configureRoutingBehavior: "Configure routing behavior for this reseller",
    failoverEnabled: "Failover Enabled",
    automaticallyTryNextProvider: "Automatically try next provider on failure",
    priorityProvidersInOrder: "Priority Providers (in order)",
    featurePermissions: "Feature Permissions",
    controlWhatActionsReseller: "Control what actions this reseller can perform",
    visibleDashboardPages: "Visible Dashboard Pages",
    controlWhichPagesReseller: "Control which pages the reseller can access",
    resellerApiKeysForIntegration: "Reseller API keys for integration",
    apiKeysAutoGenerated: "API keys are auto-generated. Share these securely with the reseller.",
    operationalLimits: "Operational Limits",
    manageResellerCapabilitiesAndLimits: "Manage reseller capabilities and limits",
    updateReseller: "Update Reseller",
    createResellerButton: "Create Reseller",

    // Global Transactions
    globalTransactions: "Global Transactions",
    transactionsAcrossEntities: "transactions across all entities",
    filters: "Filters",
    clearAll: "Clear All",
    searchIdRefMerchant: "Search ID...",
    allEngines: "All Engines",
    allResellers: "All Resellers",
    allProviders: "All Providers",
    location: "Location",
    allLocations: "All Locations",
    fromDate: "From Date",
    toDate: "To Date",
    customDateRange: "Custom Date Range",
    activeFilters: "active",
    errorLoadingTransactions: "Error loading transactions",
    pleaseCheckConnection: "Please check your connection and try again.",

    // Servers
    serverInfrastructure: "Server Infrastructure",
    realTimeMonitoring: "Real-time monitoring and health status",
    last1h: "Last 1h",
    last6h: "Last 6h",
    last24h: "Last 24h",
    last7d: "Last 7d",
    healthy: "Healthy",
    warnings: "Warnings",
    avgCpu: "Avg CPU",
    avgMemory: "Avg Memory",
    requestsPerMin: "Requests/min",
    avgLatency: "Avg Latency",
    overview: "Overview",
    performance: "Performance",
    network: "Network",
    cpuUsage: "CPU Usage (24h)",
    memoryUsage: "Memory Usage (24h)",
    responseLatency: "Response Latency (24h)",
    networkTraffic: "Network Traffic by Server",
    cpu: "CPU",
    memory: "Memory",
    disk: "Disk",
    latency: "Latency",
    uptime: "Uptime",
    reqPerMin: "Req/min",
    viewLogs: "View Logs",
    requestsPerMin24h: "Requests/min (24h)",
    disabled: "Disabled",
    warning: "Warning",
    critical: "Critical",
    offline: "Offline",
    serverLogs: "Server Logs",
    serverLogsTitle: "Server Logs - {name}",
    viewServerLogsDescription: "View server logs including startup time, database connections, memory usage, and request status",
    loadingLogs: "Loading logs...",
    serverStarted: "Server Started",
    databaseConnectionEstablished: "Database Connection Established",
    currentMemoryUsage: "Current Memory Usage",
    totalLogEntries: "Total Log Entries",
    filterByStatus: "Filter by Status",
    allStatuses: "All Statuses",
    statusUp: "UP",
    statusLow: "LOW",
    statusCritical: "CRITICAL",
    statusDown: "DOWN",
    recentLogEntries: "Recent Log Entries",
    showingEntries: "Showing {filtered} of {total} entries",
    showingEntriesStatusChanges: "Showing {filtered} of {total} entries (status changes only)",
    latencyLabel: "Latency",
    noLogsAvailable: "No logs available",
    connectionFailed: "Connection failed - Request may have been queued or failed",
    criticalLatencyDetected: "Critical latency detected - Immediate attention required",
    highLatencyDetected: "High latency detected - Performance may be degraded",
    apiGateway: "API Gateway",
    bankserver: "Bank Server",
    paymentProcessor: "Payment Processor",
    databasePrimary: "Database Primary",
    databaseReplica: "Database Replica",
    webhookHandler: "Webhook Handler",
    queueProcessor: "Queue Processor",
    loadBalancer: "Load Balancer",
    redisCache: "Redis Cache",
    singapore: "Singapore",
    bangkok: "Bangkok",
    na: "N/A",
    failedToFetchLogs: "Failed to fetch logs",
    performanceCPUGraph: "CPU",
    performanceMemoryGraph: "Memory",
    performanceRequestsGraph: "Requests",
    performanceLatencyGraph: "Latency",

    // Merchant Panel
    merchantPortal: "Merchant Portal",
    navigation: "Navigation",
    loggedInAs: "Logged in as",

    // Merchant Dashboard

    // Merchant Settings
    apiKeys: "API Keys",
    yourAuthCredentials: "Your authentication credentials for API access",
    apiKeyPublic: "API Key (Public)",
    secretKey: "Secret Key",
    regenerateKeys: "Regenerate Keys",
    invalidateCurrentKeys: "This will invalidate your current keys",
    regenerate: "Regenerate",
    regenerateApiKeys: "Regenerate API Keys?",
    invalidateImmediately: "This action will invalidate your current API keys immediately. All existing integrations using these keys will stop working until updated.",
    callbackUrls: "Callback URLs",
    whereWeSendNotifications: "Where we send payment notifications",
    callbackUrl: "Callback URL",
    wePostToEndpoint: "We POST transaction updates to this endpoint",
    webhookUrl: "Webhook URL (Optional)",
    additionalEventNotifications: "For additional event notifications",
    saveUrls: "Save URLs",
    merchantProfile: "Merchant Profile",
    yourBusinessInfo: "Your business information",
    businessName: "Business Name",
    emailAddress: "Email Address",
    supportContact: "Support Contact",
    saveProfile: "Save Profile",

    // Engine Panel
    enginePanel: "Engine Panel",

    // Reseller Panel
    resellerPanel: "Reseller Panel",

    // Common form labels
    name: "Name",
    email: "Email",
    phone: "Phone",
    contactEmail: "Contact Email",

    // API related
    keepSecretConfidential: "Keep your secret key confidential.",
    securityNotice: "Security Notice",
    neverExposeSecretKey: "Never expose it in client-side code or share it publicly.",
    regenerateAllKeys: "Regenerate All Keys",
    invalidateCurrentKeysWarning: "Generate new API keys. Your current keys will stop working immediately.",

    // Withdrawals
    manualPayout: "Manual Payout",
    newWithdrawal: "New Withdrawal",
    history: "History",
    withdrawalRequestSubmittedSuccessfully: "Withdrawal request submitted successfully!",
    insufficientBalance: "Insufficient balance",
    invalidAmount: "Invalid amount",
    invalidWalletAddress: "Invalid wallet address",
    invalidBankAccount: "Invalid bank account details",
    unauthorizedRequest: "Unauthorized request",
    forbiddenRequest: "Access denied",
    somethingWentWrong: "Something went wrong",
    noWithdrawalsFound: "No withdrawals found",
    bankAccountNumber: "Bank Account Number",
    accountHolderName: "Account Holder Name",
    enterAccountHolderName: "Enter account holder name",
    orderId: "Order ID",
    customerId: "Customer ID",
    optionalCustomerRef: "Optional customer reference",
    createPayout: "Create Payout",

    // Dialogs
    areYouSure: "Are you sure?",

    // Login Forms
    pleaseEnterBothEmailAndPassword: "Please enter both email and password",
    loginFailed: "Login failed. Please check your credentials.",
    loginFailedCheckCredentials: "Login failed. Please check your credentials and panel type.",
    pleaseFillInAllFields: "Please fill in all fields",
    rememberMe: "Remember me",
    forgotPassword: "Forgot password?",
    signIn: "Sign In",
    signingIn: "Signing in...",
    signInAsStaff: "Sign In as Staff",
    signInAs: "Sign In as {type}",
    signInToPortal: "Sign In to Portal",
    selectAccountTypeAndEnterCredentials: "Select your account type and enter your credentials",
    welcomeBack: "Welcome back",
    welcomeBackAdmin: "Welcome Back",
    signInToAccessAdminDashboard: "Sign in to access the admin dashboard",
    signInToYourMerchantAccount: "Sign in to your merchant account",
    paymentGatewayAdminPanel: "Payment Gateway Admin Panel",
    accessRestrictedToAuthorizedOnly: "Access restricted to authorized personal only",
    staffMemberClickHereToLogin: "Staff Member? Click here to login",
    enginePortalAccess: "Engine Portal Access",
    partnerPortalAccess: "Partner Portal Access",
    staffMemberLogin: "Staff Member Login",
    staffAccess: "Staff Access",
    signInWithStaffCredentials: "Sign in with your staff credentials",
    panelType: "Panel Type",
    selectYourPanelType: "Select your panel type",
    selectPanelYouWereAssignedTo: "Select the panel you were assigned to",
    adminPanel: "Admin Panel",
    administrativeStaff: "Administrative staff",
    engineStaff: "Engine staff",
    resellerStaff: "Reseller staff",
    merchantStaff: "Merchant staff",
    staffAccessOnly: "Staff access only. Not a staff member?",
    adminLogin: "Admin Login",
    engineLogin: "Engine Login",
    resellerLogin: "Reseller Login",
    merchantLogin: "Merchant Login",
    iAmStaffMember: "I am a staff member",
    needHelpContact: "Need help? Contact",
    needHelpAccessingAccount: "Need help accessing your account?",
    contactSupport: "Contact Support",
    merchantBusinessAccess: "Merchant business access",
    resellerPartnerAccess: "Reseller partner access",
    allRightsReserved: "All rights reserved.",
    privacyPolicy: "Privacy Policy",
    termsOfService: "Terms of Service",

    // Infrastructure
    infrastructure: "Infrastructure",
    database: "Database",
    queue: "Queue",

    // Panel specific

    // Additional
    total: "Total",

    // Transaction fields
    transaction: "Transaction",
    method: "Method",
    ref: "Ref",
    paymentId: "Payment ID",
    clientName: "Client Name",
    clientBankAccount: "Client Bank Account",

    // Filters
    dateRange: "Date Range",
    clearFilters: "Clear Filters",
    exportCsv: "Export CSV",
    searchHeaderPlaceholder: "Search transactions, merchants...",

    // Stats
    vsLastPeriod: "vs last period",
    live: "Live",
    noTransactionsYet: "No transactions yet",
    noProvidersConfigured: "No providers configured",
    noProvidersFound: "No providers found",
    avgResponse: "Avg Response",

    // Routing Rule Form fields

    // Withdrawal form
    selectMerchant: "Select merchant",
    selectBank: "Select bank",

    // Priority badge text
    priorityText: "Priority",
    viewOnlyBadge: "View",

    // Additional transaction/common
    retry: "Retry",
    noTransactionsFound: "No transactions found",
    noDeposits: "No deposits",

    // Transaction Detail
    transactionDetails: "Transaction Details",
    transactionInfo: "Transaction Info",
    processingInfo: "Processing Info",
    customerInfo: "Customer Info",
    bankDetails: "Bank Details",
    callbackStatus: "Callback Status",
    timestamps: "Timestamps",
    retryTransaction: "Retry Transaction",
    resendCallback: "Resend Callback",
    routingAttempts: "Routing Attempts",
    attempts: "Attempts",
    lastAttempt: "Last Attempt",
    createdAt: "Created",
    processedAt: "Processed",
    completedAt: "Completed",
    updatedAt: "Updated At",
    currency: "Currency",
    feeAmount: "Fee",
    providerRef: "Provider Ref",
    account: "Account",
    nA: "N/A",
    errorCode: "Error Code",
    qrCode: "QR Code",
    qrPayment: "QR Payment",
    qrPaymentInfo: "QR Payment Info",
    qrCodeInfo: "QR Code Info",
    qrCodeDetails: "QR Code Details",
    qrCodePayment: "QR Code Payment",
    expiryInfo: "Expiry Info",
    expiresAt: "Expires At",
    viewQrCode: "View QR Code",
    expires: "Expires",
    manualStatusUpdate: "Manual Status Update",
    updateStatus: "Update Status",
    newStatus: "New Status",
    adminNoteOptional: "Admin Note (Optional)",
    adminNote: "Admin Note",
    reasonForStatusChange: "Reason for status change...",
    characters: "characters",
    updating: "Updating...",
    statusUpdated: "Status updated",
    transactionStatusChangedTo: "Transaction status changed to",
    updateFailed: "Update failed",
    failedToUpdateTransactionStatus: "Failed to update transaction status",
    webhookInfo: "Webhook Info",
    webhook: "Webhook",
    url: "URL",
    rawWebhookData: "Raw Webhook Data",
    updated: "Updated",
    ip: "IP",
    createdLabel: "Created",
    hierarchy: "Hierarchy",
    delivered: "Delivered",
    callback: "Callback",

    // Withdrawal Form
    bankTransfer: "Bank Transfer",
    usdtCrypto: "USDT Crypto",
    selectBankLabel: "Select Bank",
    chooseYourBank: "Choose your bank",
    accountNumber: "Account Number",
    enterAccountNumber: "Enter your bank account number",
    amountThb: "Amount (PKR)",
    minimumWithdrawal: "Minimum withdrawal",
    withdrawToBank: "Withdraw to Bank",
    networkLabel: "Network",
    walletAddress: "Wallet Address",
    enterWalletAddress: "Enter your {network} USDT address",
    amountUsdt: "Amount (USDT)",
    thbEquivalent: "PKR Equivalent",
    exchangeRate: "Exchange Rate",
    withdrawUsdt: "Withdraw USDT",
    withdrawalSummary: "Withdrawal Summary",
    totalWithdrawn: "Total Withdrawal",
    requests: "requests",
    processingTime: "Processing Time",
    hours: "hours",
    mins: "mins",
    needHelp: "Need Help?",
    walletWarning: "Please ensure the wallet address is correct and supports",
    incorrectAddressWarning: "Incorrect addresses may result in permanent loss of funds.",

    // Merchant Form
    editMerchant: "Edit Merchant",
    addNewMerchant: "Add New Merchant",
    merchantName: "Merchant Name",
    merchantCode: "Merchant Code",
    merchantInfo: "Merchant Info",
    directAdmin: "Direct (Admin)",
    enableDeposits: "Enable Deposits",
    allowDepositTransactions: "Allow deposit transactions",
    enableWithdrawals: "Enable Withdrawals",
    allowWithdrawalTransactions: "Allow withdrawal transactions",
    apiSettings: "API Settings",
    limitsFees: "Limits & Fees",
    contact: "Contact",
    securityNote: "API keys are shown only once. Store them securely.",
    ipWhitelist: "IP Whitelist",
    email: "email",

    noIpsWhitelisted: "No IPs whitelisted",
    rateLimit: "Rate Limit (requests/minute)",
    minDeposit: "Min Deposit",
    maxDeposit: "Max Deposit",
    minWithdrawal: "Min Withdrawal",
    maxWithdrawal: "Max Withdrawal",
    dailyDepositLimit: "Daily Deposit Limit",
    dailyWithdrawalLimit: "Daily Withdrawal Limit",
    feeType: "Fee Type",
    feeValue: "Fee Value",
    percentage: "Percentage",
    fixed: "Fixed",
    tiered: "Tiered",
    settlementPeriod: "Settlement Period",
    sameDay: "Same Day",
    nextDay: "Next Day",
    weekly: "Weekly",
    updateMerchant: "Update Merchant",
    createMerchantBtn: "Create Merchant",
    emailCannotBeChanged: "Email cannot be changed",
    password: "Password",
    changePassword: "Change Password",
    leaveBlankToKeepPassword: "Leave blank to keep current password",
    changePasswordDescription: "Enter a new password to change it, or leave blank to keep the current password",
    selectEngine: "Select Engine",
    selectReseller: "Select Reseller",
    whoManagesMerchant: "Who manages this merchant",
    internalNotes: "Internal notes...",
    apiKeysShownOnce: "API keys are shown only once. Store them securely.",
    ipWhitelistPlaceholder: "e.g., 192.168.1.1",
    minDepositThb: "Min Deposit (PKR)",
    maxDepositThb: "Max Deposit (PKR)",
    minWithdrawalThb: "Min Withdrawal (PKR)",
    maxWithdrawalThb: "Max Withdrawal (PKR)",
    dailyDepositLimitThb: "Daily Deposit Limit (PKR)",
    dailyWithdrawalLimitThb: "Daily Withdrawal Limit (PKR)",
    feeValueLabel: "Fee Value",
    backButton: "← Back",
    nextButton: "Next →",
    merchantNamePlaceholder: "e.g., Gaming Site A",
    merchantCodePlaceholder: "e.g., MGC001",
    emailPlaceholder: "merchant@example.com",
    passwordPlaceholder: "Enter password",
    callbackUrlPlaceholder: "https://your-site.com/api/payment/callback",
    webhookUrlPlaceholder: "https://your-site.com/api/payment/webhook",
    contactNamePlaceholder: "John Doe",
    contactEmailPlaceholder: "john@example.com",
    contactPhonePlaceholder: "+66 XXX XXX XXXX",
    t0SameDay: "T+0 (Same Day)",
    t1NextDay: "T+1 (Next Day)",
    t3: "T+3",
    t7Weekly: "T+7 (Weekly)",
    createMerchantUnderReseller: "Create a new merchant under your reseller account",
    commissionGivenToYouBy: "This is the commission rate given to you by {name}. You can assign a commission to this merchant.",
    feeValueSetToBy: "This fee value is set to {value}% (the commission rate given to you by {name}). This cannot be changed.",
    apisettingsapiKeyPlaceholder: "Leave blank to keep current API Key",
    apisettingssecretKeyPlaceholder: "Leave blank to keep current Secret Key",

    // Provider Form
    editProvider: "Edit Provider",
    addNewProvider: "Add New Provider",
    providerNameRequired: "Provider Name *",
    providerCodeRequired: "Provider Code *",
    providerNamePlaceholder: "e.g., SCB",
    providerCodePlaceholder: "e.g., SCB",
    providerTypeEwallet: "E-Wallet",
    providerTypeQr: "QR Payment",
    providerTypeCard: "Card",
    providerName: "Provider Name",
    providerCode: "Provider Code",
    supportsDeposits: "Supports Deposits",
    processDepositTransactions: "Process deposit transactions",
    supportsWithdrawals: "Supports Withdrawals",
    processWithdrawalTransactions: "Process withdrawal transactions",
    supportsQr: "Supports QR Payments",
    generatePromptPayQr: "Generate PromptPay QR codes",
    logoUrl: "Logo URL",
    logoUrlPlaceholder: "https://...",
    apiConfig: "API Config",
    routingLimits: "Routing & Limits",
    apiEndpoint: "API Endpoint",
    apiEndpointPlaceholder: "https://api.provider.com/v1",
    apiKey: "API Key",
    apiSecret: "API Secret",
    webhookSecret: "Webhook Secret",
    providerIpWhitelist: "Provider IP Whitelist (for webhooks)",
    priorityHighest: "Priority (1 = highest)",
    weightLoadBalancing: "Weight (load balancing)",
    dailyLimit: "Daily Limit",
    minAmountThb: "Min Amount (PKR)",
    maxAmountThb: "Max Amount (PKR)",
    dailyLimitThb: "Daily Limit (PKR)",
    feePercentage: "Fee Percentage",
    settlementTimeLabel: "Settlement Time",
    instant: "Instant",
    oneHour: "1 Hour",
    updateProvider: "Update Provider",
    addProviderBtn: "Add Provider",

    // Common

    // Engine Panel specific
    last7DaysVolume: "Last 7 days transaction volume",
    providerDistribution: "Provider Distribution",
    recent: "Recent",
    totalTransactions: "Total Transactions",
    providerDistribution: "Provider Distribution",
    recent20Transactions: "Recent 20 Transactions",
    searchByIdOrReference: "Search by ID or reference...",
    resellersCreatedByThisEngine: "Resellers Created by This Engine",
    loginLogs: "Login Logs",
    apiUsageLogs: "API Usage Logs",
    additionalSettings: "Additional Settings",
    operationalLimitsAndPaymentMethods: "Operational limits and payment methods",
    configureDepositAndWithdrawalFeesForThisEngine: "Configure deposit and withdrawal fees for this engine",
    configureDepositAndWithdrawalFees: "Configure deposit and withdrawal fees with commission rates",
    depositAndWithdrawal: "Deposit & Withdrawal",
    commission: "Commission",
    constraints: "Constraints",
    depositAndWithdrawalFees: "Deposit & Withdrawal Fees",
    depositFee: "Deposit Fee",
    withdrawalFee: "Withdrawal Fee",
    fixedAmount: "Fixed Amount",
    commissionRates: "Commission Rates",
    commissionEarnedFromTransactions: "Commission earned from transactions",
    calculateFeePreview: "Calculate Fee Preview",
    calculating: "Calculating...",
    feeBreakdownFor1000: "Fee Breakdown for 1000RS:",
    adminFee: "Admin Fee",
    feeConstraints: "Fee Constraints",
    setMinMaxLimitsForChildEntities: "Set min/max limits for child entities",
    depositConstraints: "Deposit Constraints",
    withdrawalConstraints: "Withdrawal Constraints",
    commissionConstraints: "Commission Constraints",
    minFee: "Min Fee",
    maxFee: "Max Fee",
    minDepositCommission: "Min Deposit Commission",
    maxDepositCommission: "Max Deposit Commission",
    minWithdrawalCommission: "Min Withdrawal Commission",
    maxWithdrawalCommission: "Max Withdrawal Commission",
    none: "None",
    noMerchantsMatchSearch: "No merchants match your search",
    todaysVolume: "Today's Volume",
    volumeTrend7Days: "Volume Trend (7 Days)",
    apiAndSettings: "API & Settings",
    avgDeposit: "Total Deposit",
    searchMerchantsPlaceholder: "Search merchants...",
    merchantsRegisteredCount: "merchants registered",
    partnersCount: "partners",
    addReseller: "Add Reseller",
    noResellers: "No resellers",
    viewOnly: "View Only",
    paymentProviderStatus: "Payment provider status (managed by admin)",
    routingExplanation: "Rules are evaluated in priority order. The first matching rule determines which providers handle the transaction. If a provider fails, the system automatically fails over to the next in the sequence.",
    createFirstRoutingRule: "Create your first routing rule to start directing transactions",
    logs: "logs",
    noLogs: "No logs",
    payload: "Payload",
    noSettlementsFound: "No settlements",
    requestPayoutsViaBankUsdt: "Request payouts via Bank or USDT",
    clear: "Clear",
    feeConfiguration: "Fee Configuration",
    percentageFeeLabel: "Percentage Fee (%)",
    commissionRatesAndFeeInfo: "Commission rates and fee information",
    ratesAssignedToYou: "These are the rates assigned to you",
    assignedBy: "Assigned by",
    createNewMerchant: "Create New Merchant",
    businessNameLabel: "Business Name",
    emailLabel: "Email",
    callbackUrlLabel: "Callback URL",
    // Additional Merchant Panel
    newDeposit: "New Deposit",
    generateQrDeposit: "Generate QR Deposit",
    createPromptPayQr: "Create PromptPay QR code for customers",
    howItWorks: "How it works",
    enterDepositAmount: "Enter the deposit amount",
    generateQr: "Generate QR Code",
    customerScansQr: "Customer scans QR",
    paymentProcessed: "Payment processed",
    waitingForPayment: "Waiting for payment",
    statusAutoRefresh: "Status will auto-refresh",
    paymentReceived: "Payment Received",
    qrCodeExpired: "QR Code Expired",
    generateNewQr: "Generate New QR",

    // Transaction Detail Sheet
    statusTimeline: "Status Timeline",
    processingLabel: "Processing",
    completedLabel: "Completed",
    failedLabel: "Failed",
    error: "Error",
    reference: "Reference",

    // Provider Page

    // Settlement Page
    pendingSettlement: "Pending Settlement",
    totalRecords: "Total Records",
    last7DaysPerformance: "Last 7 days performance",
    transactionPerformance: "Transaction performance",
    welcomeBackOverview: "Welcome back! Here's your overview.",
    noEmail: "No email",

    // Additional missing translations
    searchByWebhookId: "Search by webhook ID, transaction ID, source...",
    selectPanelType: "Select your panel type",
    enterNewPassword: "Enter new password",
    searchRoles: "Search roles...",
    searchStaff: "Search staff members...",
    searchBySettlementId: "Search by settlement ID...",
    searchBySettlementIdPlaceholder: "Search by settlement ID...",
    searchByWithdrawalIdAccount: "Search by withdrawal ID, account...",
    searchByTransactionId: "Search by transaction ID...",
    defaultQrRoute: "e.g., Default QR Route",
    describeRole: "Describe what this role can do...",
    orderIdPlaceholder: "Order ID, Customer ID, etc.",
    amountPlaceholder: "0.00",
    accountNumberPlaceholder: "e.g., 1234567890",
    accountHolderPlaceholder: "Full name as on bank account",
    phonePlaceholder: "+66 XXX XXX XXXX",
    orderIdLabel: "Order ID",
    customerIdLabel: "Customer ID",
    customerIdPlaceholder: "Enter Customer ID",


    // Admin Roles
    staffRoles: "Staff Roles",
    rolesCount: "roles",
    customCount: "custom",
    systemCount: "system",
    createRole: "Create Role",
    editRole: "Edit Role",
    updateRoleDetailsAndPermissions: "Update role details and permissions",
    defineNewRoleWithPermissions: "Define a new role with specific permissions",
    basicDetailsForRole: "Basic details for the role",
    roleNameRequired: "Role Name *",
    merchantManagerPlaceholder: "e.g., Merchant Manager",
    describeWhatRoleCanDo: "Describe what this role can do...",
    roleCreatedForAdminPanel: "This role will be created for the Admin panel",
    selectActionsRoleCanPerform: "Select what actions this role can perform",
    selectAll: "Select All",
    deselectAll: "Deselect All",
    noPermissionsSelected: "No permissions selected",
    permissionsSelected: "{count} permissions selected",
    permissionSelected: "1 permission selected",
    staffWithRoleActions: "Staff members with this role will be able to perform these actions",
    updateRole: "Update Role",
    provideRoleNameAndPermission: "Please provide a role name and select at least one permission",
    roleEditingNotImplemented: "Role editing is not yet implemented. Please delete and recreate the role.",
    searchRolesPlaceholder: "Search roles...",
    customRoles: "Custom Roles",
    rolesYouveCreated: "Roles you've created",
    role: "Role",
    permissions: "Permissions",
    noPermissions: "No permissions",
    morePermissions: "more",
    systemRoles: "System Roles",
    predefinedRolesReadOnly: "Predefined roles (read-only)",
    systemBadge: "System",
    noRolesFound: "No roles found",
    tryDifferentSearchTerm: "Try a different search term",
    getStartedCreatingFirstRole: "Get started by creating your first role",
    deleteRole: "Delete Role",
    deleteRoleConfirmation: "Are you sure you want to delete the role \"{name}\"? This action cannot be undone. Staff members with this role will lose access.",
    systemRolesCannotBeDeleted: "System roles cannot be deleted",
    panelTypeRequired: "Panel Type *",
    permissionsTitle: "Permissions",
    createRoleAction: "Create Role",

    // Admin Reseller
    editReseller: "Edit Reseller",
    generatePassword: "Generate Password",

    providerTransactionId: "Provider Transaction ID",
    systemId: "System ID",
    processorBankcode: "Processor Bankcode",
    payoutType: "Payout Type",
    country: "Country",
    platform: "Platform",
    ipAddress: "IP Address",
    latestResponse: "Latest Response",
    copiedToClipboard: "Copied to clipboard",
    passwordGeneratedAndCopied: "Password generated and copied to clipboard",
    apiKeysRegenerated: "API Keys regenerated successfully",
  },

  th: {
    // Common
    dashboard: "แดชบอร์ด",
    transactions: "ธุรกรรม",
    deposits: "ฝากเงิน",
    withdrawals: "ถอนเงิน",
    merchants: "ร้านค้า",
    providers: "ผู้ให้บริการ",
    routing: "การกำหนดเส้นทาง",
    webhooks: "เว็บฮุค",
    settlements: "การชำระบัญชี",
    settings: "การตั้งค่า",
    engines: "เอนจิ้น",
    resellers: "ตัวแทนจำหน่าย",
    servers: "เซิร์ฟเวอร์",
    allTransactions: "ธุรกรรมทั้งหมด",
    staff: "พนักงาน",
    staffMembers: "สมาชิกพนักงาน",
    staffMembersCount: "สมาชิกพนักงาน",
    staffMember: "สมาชิกพนักงาน",
    createStaffMember: "สร้างสมาชิกพนักงาน",
    noStaffMembersFound: "ไม่พบสมาชิกพนักงาน",
    noRolesAssigned: "ไม่มีบทบาทที่กำหนด",
    changeStaffStatus: "เปลี่ยนสถานะพนักงาน",
    deleteStaffMember: "ลบสมาชิกพนักงาน",
    areYouSureYouWantTo: "คุณแน่ใจหรือไม่ว่าต้องการ",
    actions: "การดำเนินการ",
    roles: "บทบาท",
    description: "คำอธิบาย",
    analytics: "การวิเคราะห์",
    searchByIdOrReference: "ค้นหาโดยใช้รหัสประจำตัวหรือหมายเลขอ้างอิง...",
    successRate: "อัตราความสำเร็จ",
    loginLogs: "บันทึกการเข้าสู่ระบบ",
    apiUsageLogs: "บันทึกการใช้งาน API",


    // Actions
    save: "บันทึก",
    cancel: "ยกเลิก",
    delete: "ลบ",
    edit: "แก้ไข",
    add: "เพิ่ม",
    create: "สร้าง",
    update: "อัปเดต",
    view: "ดู",
    search: "ค้นหา",
    filter: "ตัวกรอง",
    export: "ส่งออก",
    import: "นำเข้า",
    refresh: "รีเฟรช",
    reset: "รีเซ็ต",
    submit: "ส่ง",
    confirm: "ยืนยัน",
    close: "ปิด",
    back: "ย้อนกลับ",
    next: "ถัดไป",
    previous: "ก่อนหน้า",
    loading: "กำลังโหลด...",
    saving: "กำลังบันทึก...",
    creating: "กำลังสร้าง...",
    processing: "กำลังประมวลผล...",
    postMethod: "โพสต์",
    getMethod: "ดึงข้อมูล",

    // Status
    active: "ใช้งาน",
    inactive: "ไม่ใช้งาน",
    pending: "รอดำเนินการ",
    success: "สำเร็จ",
    failed: "ล้มเหลว",
    declined: "ถูกปฏิเสธ",
    expired: "หมดอายุ",
    unknown: "ไม่ทราบ",
    cancelled: "ยกเลิกแล้ว",
    suspended: "ถูกระงับ",
    terminated: "ยุติ",
    completed: "เสร็จสิ้น",
    maintenance: "บำรุงรักษา",
    degraded: "ประสิทธิภาพลดลง",
    ewallet: "อีวอลเล็ต",
    card: "บัตร",
    qr: "คิวอาร์",
    bank: "ธนาคาร",

    // Time
    today: "วันนี้",
    thisDate: "วันนี้",
    thisWeek: "สัปดาห์นี้",
    thisMonth: "เดือนนี้",
    thisYear: "ปีนี้",
    customRange: "ช่วงกำหนดเอง",
    selectRange: "เลือกช่วง",

    // Navigation
    logout: "ออกจากระบบ",
    profile: "โปรไฟล์",
    notifications: "การแจ้งเตือน",
    markAllRead: "ทำเครื่องหมายว่าอ่านแล้วทั้งหมด",
    newNotifications: "ใหม่",
    noUnreadNotifications: "ไม่มีการแจ้งเตือนที่ยังไม่ได้อ่าน",
    viewAllNotifications: "ดูการแจ้งเตือนทั้งหมด",
    notificationMarkedAsRead: "ทำเครื่องหมายการแจ้งเตือนว่าอ่านแล้ว",
    notificationRemoved: "ลบการแจ้งเตือนแล้ว",
    allNotificationsMarkedRead: "ทำเครื่องหมายการแจ้งเตือนทั้งหมดว่าอ่านแล้ว",
    failedToLoadNotifications: "ไม่สามารถโหลดการแจ้งเตือนได้",
    errorMarkingAsRead: "ไม่สามารถทำเครื่องหมายว่าอ่านแล้วได้",
    errorRemovingNotification: "ไม่สามารถลบการแจ้งเตือนได้",
    allNotificationsAlreadyRead: "อ่านการแจ้งเตือนทั้งหมดแล้ว",
    someNotificationsFailedToMarkRead: "ไม่สามารถทำเครื่องหมายบางการแจ้งเตือนว่าอ่านแล้วได้",
    failedToMarkAsRead: "ไม่สามารถทำเครื่องหมายว่าอ่านแล้วได้",
    noNotificationsToClear: "ไม่มีการแจ้งเตือนที่จะล้าง",
    allNotificationsCleared: "ล้างการแจ้งเตือนทั้งหมดแล้ว",
    someNotificationsCouldNotBeRemoved: "ไม่สามารถลบบางการแจ้งเตือนได้",
    failedToRemoveNotification: "ไม่สามารถลบการแจ้งเตือนได้",
    allNotifications: "ดูการแจ้งเตือนทั้งหมดรวมถึงการถอนและการฝากเงิน",
    lastUpdated: "อัปเดตล่าสุด",
    notUpdatedYet: "ยังไม่ได้อัปเดต",
    searchForNotifications: "ค้นหาการแจ้งเตือน...",
    noNotifications: "ไม่มีการแจ้งเตือน",
    successful: "สำเร็จ",
    systemNotification: "ระบบ",
    all: "ทั้งหมด",
    read: "อ่านแล้ว",
    unread: "ยังไม่ได้อ่าน",
    newWithdrawalNotification: "การถอนเงินใหม่",
    newDepositNotification: "การฝากเงินใหม่",
    withdrawalSuccess: "ถอนเงินสำเร็จ",
    depositSuccess: "ฝากเงินสำเร็จ",
    withdrawalRequested: "คำขอถอนเงิน",
    welcome: "ยินดีต้อนรับ",
    requestedWithdrawal: "ขอถอนเงิน",
    receivedDeposit: "ได้รับการฝากเงิน",
    yourWithdrawalRequestOf: "คำขอถอนเงินของคุณจำนวน",
    hasBeenReceivedPendingReview: "ได้รับแล้วและกำลังรอการตรวจสอบ",

    // Global Transactions
    globalTransactions: "ธุรกรรมทั้งหมด (ทั่วระบบ)",

    // Dashboard
    paymentOrchestration: "ภาพรวมการประสานงานการชำระเงิน",
    balance: "ยอดคงเหลือ",
    adminBalance: "ยอดคงเหลือแอดมิน",
    totalDeposits: "ยอดฝากทั้งหมด",
    totalWithdrawals: "ยอดถอนทั้งหมด",
    successRate: "อัตราความสำเร็จ",
    activeMerchants: "ร้านค้าที่ใช้งานอยู่",
    quickActions: "การทำงานด่วน",
    addMerchant: "เพิ่มร้านค้า",
    onboardNewMerchant: "เพิ่มร้านค้าใหม่",
    configureProvider: "ตั้งค่าผู้ให้บริการ",
    addPaymentProvider: "เพิ่มผู้ให้บริการชำระเงิน",
    routingRules: "กฎการกำหนดเส้นทาง",
    configureRouting: "ตั้งค่าการกำหนดเส้นทาง",
    pendingTransactions: "ธุรกรรมที่รอดำเนินการ",
    requiresAttention: "ต้องการการตรวจสอบ",
    volumeOverview: "ภาพรวมปริมาณ",
    last7Days: "ปริมาณธุรกรรม 7 วันล่าสุด",
    recentTransactions: "ธุรกรรมล่าสุด",
    viewAll: "ดูทั้งหมด",
    viewMore: "ดูเพิ่มเติม",
    totalVolume: "ปริมาณรวม",
    volumeOverviewTitle: "ภาพรวมปริมาณ",
    transactionVolume: "ปริมาณธุรกรรม",
    allStatus: "สถานะทั้งหมด",
    allCurrencies: "สกุลเงินทั้งหมด",
    transactionsAcrossEntities: "ธุรกรรมจากทุกหน่วยงาน",
    filters: "ตัวกรอง",
    clearAll: "ล้างทั้งหมด",
    searchIdRefMerchant: "ค้นหา ID...",
    allEngines: "เอนจิ้นทั้งหมด",
    allResellers: "ตัวแทนจำหน่ายทั้งหมด",
    allProviders: "ผู้ให้บริการทั้งหมด",
    allMerchants: "ร้านค้าทั้งหมด",
    allCurrencies: "สกุลเงินทั้งหมด",
    location: "สถานที่",
    allLocations: "ทุกสถานที่",
    fromDate: "จากวันที่",
    toDate: "ถึงวันที่",
    customDateRange: "ช่วงวันที่กำหนดเอง",
    activeFilters: "ตัวกรองที่ใช้งาน",
    errorLoadingTransactions: "เกิดข้อผิดพลาดในการโหลดธุรกรรม",
    pleaseCheckConnection: "โปรดตรวจสอบการเชื่อมต่อและลองอีกครั้ง",

    // Transactions
    transactionsFound: "พบธุรกรรม",
    transactionId: "รหัสธุรกรรม",
    merchantRef: "อ้างอิงร้านค้า",
    type: "ประเภท",
    amount: "จำนวนเงิน",
    status: "สถานะ",
    date: "วันที่",
    provider: "ผู้ให้บริการ",
    deposit: "ฝากเงิน",
    withdrawal: "ถอนเงิน",
    showing: "แสดง",
    to: "ถึง",
    of: "จาก",
    page: "หน้า",

    // Merchants
    merchantsRegistered: "ร้านค้าที่ลงทะเบียน",
    searchMerchants: "ค้นหาร้านค้า...",
    searchMerchantsByIdEmailName: "ค้นหาด้วยรหัส อีเมล หรือชื่อ...",
    merchant: "ร้านค้า",
    code: "โค้ด",
    parent: "ต้นสังกัด",
    features: "ฟีเจอร์",
    fee: "ค่าธรรมเนียม",
    settlement: "การชำระบัญชี",
    direct: "โดยตรง",
    viewDetails: "ดูรายละเอียด",
    viewApiKeys: "ดูคีย์ API",
    suspend: "ระงับ",
    activate: "เปิดใช้งาน",
    deleteMerchant: "ลบร้านค้า",
    deleteConfirmation: "คุณแน่ใจหรือไม่ว่าต้องการลบ",
    cannotBeUndone: "การดำเนินการนี้ไม่สามารถย้อนกลับได้",
    suspendMerchantTitle: "ระงับร้านค้า",
    activateMerchantTitle: "เปิดใช้งานร้านค้า",
    suspendMerchantConfirm: "คุณแน่ใจหรือไม่ว่าต้องการระงับ",
    activateMerchantConfirm: "คุณแน่ใจหรือไม่ว่าต้องการเปิดใช้งาน",
    willNotProcessTransactions: "พวกเขาจะไม่สามารถประมวลผลธุรกรรมได้",
    noMerchantsFound: "ไม่พบร้านค้า",
    addMerchant: "เพิ่มร้านค้า",

    // Providers
    paymentProviders: "ผู้ให้บริการชำระเงิน",
    activeProviders: "ผู้ให้บริการที่ใช้งาน",
    searchProviders: "ค้นหาผู้ให้บริการ...",
    addProvider: "เพิ่มผู้ให้บริการ",
    healthScore: "คะแนนสุขภาพ",
    response: "การตอบสนอง",
    priority: "ลำดับความสำคัญ",
    disable: "ปิดใช้งาน",
    enable: "เปิดใช้งาน",
    deleteProvider: "ลบผู้ให้บริการ",
    affectRouting: "การดำเนินการนี้มีผลต่อกฎการกำหนดเส้นทาง",

    // Routing
    routingRulesTitle: "กฎการกำหนดเส้นทาง",
    configureProviderSelection: "กำหนดการเลือกผู้ให้บริการและการสลับสำรอง",
    addRule: "เพิ่มกฎ",
    howRoutingWorks: "การกำหนดเส้นทางทำงานอย่างไร",
    routingDescription: "ระบบประเมินกฎตามลำดับความสำคัญ กฎที่ตรงเงื่อนไขแรกจะกำหนดผู้ให้บริการ",
    noRoutingRules: "ยังไม่มีกฎการกำหนดเส้นทาง",
    viewTransactionRoutingConfig: "ดูการกำหนดเส้นทางของธุรกรรม",
    failover: "สลับสำรอง",
    amountRange: "ช่วงจำนวนเงิน",
    createFirstRule: "สร้างกฎแรกเพื่อเริ่มกำหนดเส้นทาง",
    defaultRule: "กฎเริ่มต้น",
    ruleName: "ชื่อกฎ",
    defaultQrRoutePlaceholder: "เช่น เส้นทาง QR เริ่มต้น",
    transactionType: "ประเภทธุรกรรม",
    paymentMethod: "วิธีชำระเงิน",
    minAmount: "จำนวนขั้นต่ำ",
    maxAmount: "จำนวนสูงสุด",
    specificMerchant: "ร้านค้าเฉพาะ (ไม่บังคับ)",
    routingMode: "โหมดการกำหนดเส้นทาง",
    providerSequence: "ลำดับผู้ให้บริการ (คลิกเพื่อเพิ่ม/ลบ)",
    clickProvidersOrder: "คลิกผู้ให้บริการตามลำดับที่ต้องการใช้ ตัวเลขคือความสำคัญ",
    enableFailover: "เปิดใช้งานการสลับสำรอง",
    autoTryNextProvider: "ลองผู้ให้บริการถัดไปโดยอัตโนมัติเมื่อล้มเหลว",
    maxFailoverAttempts: "จำนวนครั้งสลับสำรองสูงสุด",
    allTypes: "ทุกประเภท",
    depositsOnly: "เฉพาะฝากเงิน",
    withdrawalsOnly: "เฉพาะถอนเงิน",
    allMethods: "ทุกวิธี",
    priorityMode: "ลำดับความสำคัญ (ใช้รายแรกที่พร้อม)",
    roundRobin: "วนรอบ (กระจายเท่าๆ กัน)",
    weighted: "ถ่วงน้ำหนัก (ตามค่าน้ำหนักผู้ให้บริการ)",
    leastLoad: "ภาระน้อยสุด (ตามปริมาณปัจจุบัน)",
    editRoutingRule: "แก้ไขกฎการกำหนดเส้นทาง",
    createRoutingRule: "สร้างกฎการกำหนดเส้นทาง",
    deleteRoutingRule: "ลบกฎการกำหนดเส้นทาง",
    affectTransactionRouting: "อาจมีผลต่อการกำหนดเส้นทางธุรกรรม",

    // Webhook Logs
    webhookLogs: "บันทึกเว็บฮุค",
    webhookEvents: "เหตุการณ์เว็บฮุค",
    webhookId: "รหัสเว็บฮุค",
    direction: "ทิศทาง",
    source: "แหล่งที่มา",
    event: "เหตุการณ์",
    inbound: "ขาเข้า",
    outbound: "ขาออก",
    allDirection: "ทุกทิศทาง",
    allStatus: "ทุกสถานะ",
    received: "รับแล้ว",
    validated: "ตรวจสอบแล้ว",
    processed: "ประมวลผลแล้ว",
    invalidSignature: "ลายเซ็นไม่ถูกต้อง",
    invalid_signature: "ลายเซ็นไม่ถูกต้อง",
    paymentFailed: "การชำระเงินล้มเหลว",
    paymentSucceeded: "การชำระเงินสำเร็จ",
    withdrawalSucceeded: "การถอนสำเร็จ",
    withdrawalFailed: "การถอนล้มเหลว",
    webhookLog: "บันทึกเว็บฮุค",
    webhookDetails: "รายละเอียดเว็บฮุค",
    requestHeaders: "ส่วนหัวคำขอ",
    requestPayload: "ข้อมูลคำขอ",
    responseStatus: "การตอบกลับ",
    noWebhookLogs: "ไม่พบบันทึกเว็บฮุค",
    duplicate: "ซ้ำ",
    failedToLoadWebhookLogs: "ไม่สามารถโหลดบันทึกเว็บฮุค",
    noResponseBody: "ไม่มีเนื้อหาการตอบกลับ",

    // Settlements
    settlementTitle: "การชำระบัญชี",
    merchantPayoutManagement: "การจัดการจ่ายเงินให้ร้านค้า",
    createSettlement: "สร้างการชำระบัญชี",
    searchSettlements: "ค้นหาการชำระบัญชี...",
    settlementId: "รหัสการชำระบัญชี",
    period: "ช่วงเวลา",
    netAmount: "ยอดสุทธิ",
    systemCreatedAt: "สร้างโดยระบบ",
    lastModified: "แก้ไขล่าสุด",
    process: "ดำเนินการ",
    markCompleted: "ทำเครื่องหมายว่าเสร็จสิ้น",
    exportReport: "ส่งออกรายงาน",
    periodStart: "เริ่มช่วง",
    periodEnd: "สิ้นสุดช่วง",
    settlementTotalDeposits: "ยอดฝากรวม",
    settlementTotalWithdrawals: "ยอดถอนรวม",
    totalFees: "ค่าธรรมเนียมรวม",
    totalSettled: "ยอดชำระรวม",
    noSettlements: "ไม่พบการชำระบัญชี",

    // Settings
    settingsTitle: "การตั้งค่า",
    platformConfig: "การกำหนดค่าและการตั้งค่าของแพลตฟอร์ม",
    saveChanges: "บันทึกการเปลี่ยนแปลง",
    general: "ทั่วไป",
    security: "ความปลอดภัย",
    callbacks: "คอลแบ็ก",
    qrSettings: "การตั้งค่า QR",
    notificationsSettings: "การแจ้งเตือน",
    generalSettings: "การตั้งค่าทั่วไป",
    basicPlatformConfig: "การกำหนดค่าพื้นฐานของแพลตฟอร์ม",
    platformName: "ชื่อแพลตฟอร์ม",
    timezone: "เขตเวลา",
    defaultCurrency: "สกุลเงินเริ่มต้น",
    securitySettings: "การตั้งค่าความปลอดภัย",
    apiSecurityConfig: "การกำหนดค่าความปลอดภัยและการยืนยันตัวตนของ API",
    requireIpWhitelist: "ต้องใช้รายการ IP ที่อนุญาต",
    acceptApiCallsFromWhitelistedIps: "รับคำขอ API เฉพาะจาก IP ที่อยู่ในรายการอนุญาต",
    hmacAlgorithm: "อัลกอริทึม HMAC",
    signatureHeaderName: "ชื่อส่วนหัวลายเซ็น",
    timestampTolerance: "ค่าความคลาดเคลื่อนของเวลา (วินาที)",
    rejectOldRequests: "ปฏิเสธคำขอที่มีเวลาล้าสมัยเกินค่าที่กำหนด",
    callbackSettings: "การตั้งค่าคอลแบ็ก",
    configureCallback: "กำหนดพฤติกรรมคอลแบ็กของร้านค้า",
    enableCallbackRetry: "เปิดใช้งานการลองคอลแบ็กซ้ำ",
    autoRetryFailedCallbacks: "ลองคอลแบ็กที่ล้มเหลวโดยอัตโนมัติ",
    maxRetryAttempts: "จำนวนครั้งลองซ้ำสูงสุด",
    retryDelaySchedule: "ตารางเวลาหน่วงการลองซ้ำ (วินาที)",
    callbackTimeout: "หมดเวลาคอลแบ็ก (วินาที)",
    qrPaymentSettings: "การตั้งค่า QR Payment",
    promptPayConfig: "การกำหนดค่า PromptPay QR",
    defaultQrExpiry: "อายุ QR เริ่มต้น (นาที)",
    minimumAmount: "จำนวนขั้นต่ำ (PKR)",
    maximumAmount: "จำนวนสูงสุด (PKR)",
    supportedBanks: "ธนาคารที่รองรับ",
    enableInProviderSettings: "เปิดใช้งานในการตั้งค่าผู้ให้บริการ",
    notificationSettings: "การตั้งค่าการแจ้งเตือน",
    configureAlerts: "กำหนดการแจ้งเตือนและการเตือนภัย",
    emailNotifications: "การแจ้งเตือนทางอีเมล",
    sendAlertsViaEmail: "ส่งการแจ้งเตือนทางอีเมล",
    slackNotifications: "การแจ้งเตือนผ่าน Slack",
    sendAlertsToSlack: "ส่งการแจ้งเตือนไปยังช่อง Slack",
    alertThreshold: "เกณฑ์การแจ้งเตือน (PKR)",
    sendAlertAboveAmount: "ส่งการแจ้งเตือนเมื่อธุรกรรมเกินจำนวนนี้",
    alertTypes: "ประเภทการแจ้งเตือน",
    highValueTransactions: "ธุรกรรมมูลค่าสูง",
    providerFailures: "ความล้มเหลว/เสื่อมประสิทธิภาพของผู้ให้บริการ",
    failedCallbackRetries: "การลองคอลแบ็กซ้ำที่ล้มเหลว",
    suspiciousActivity: "การตรวจจับกิจกรรมต้องสงสัย",
    settingRetryDelayScheduleNote: "ค่าหน่วงเวลาที่คั่นด้วยจุลภาค: 30, 60, 300, 600, 1800",
    loadingSettings: "กำลังโหลดการตั้งค่าแพลตฟอร์ม...",
    failedToLoadSettings: "ไม่สามารถโหลดการตั้งค่าได้ โปรดลองอีกครั้งในภายหลัง",
    failedToSaveSettings: "ไม่สามารถบันทึกการตั้งค่าได้ โปรดลองอีกครั้งในภายหลัง",
    settingsSavedSuccessfully: "อัปเดตการตั้งค่าแพลตฟอร์มเรียบร้อยแล้ว",
    tryAgain: "ลองอีกครั้ง",
    error: "ข้อผิดพลาด",

    // Engine Settings
    feesAndCommission: "ค่าธรรมเนียมและคอมมิชชั่น",
    feesAndCommissionDesc: "อัตราคอมมิชชั่นและข้อมูลค่าธรรมเนียม",
    givenBy: "กำหนดโดย {type}: {name}",
    yourCommissionRates: "อัตราคอมมิชชั่นของคุณ",
    yourCommissionRatesDesc: "นี่คืออัตราที่กำหนดให้คุณ",
    depositCommission: "คอมมิชชั่นฝากเงิน",
    withdrawalCommission: "คอมมิชชั่นถอนเงิน",
    percentageFee: "ค่าธรรมเนียมแบบเปอร์เซ็นต์",
    depositCommissionPercent: "คอมมิชชั่นฝากเงิน (%)",
    withdrawalCommissionPercent: "คอมมิชชั่นถอนเงิน (%)",
    resellerProfile: "โปรไฟล์ตัวแทนจำหน่าย",
    manageProfile: "จัดการการตั้งค่าโปรไฟล์ของคุณ",
    apiCredentialsTitle: "ข้อมูลรับรอง API",
    yourApiKeysIntegration: "คีย์ API สำหรับการเชื่อมต่อของคุณ",
    regenerateApiKeysTitle: "สร้างคีย์ใหม่",
    regenerateApiKeysDesc: "การดำเนินการนี้จะยกเลิกคีย์ปัจจุบันทันที",
    regenerateApiKeysConfirm: "คุณแน่ใจหรือไม่ว่าต้องการสร้างคีย์ใหม่? การดำเนินการนี้ไม่สามารถย้อนกลับได้",

    // Engines
    enginePartners: "พาร์ทเนอร์เอนจิ้น",
    providerDistribution: "การกระจายตัวของผู้ให้บริการ",
    recent20Transactions: "ธุรกรรมล่าสุด 20 รายการ",

    searchEngines: "ค้นหาเอนจิ้น...",
    createEngine: "สร้างเอนจิ้น",
    engine: "เอนจิ้น",
    volume: "ปริมาณ",
    created: "สร้างเมื่อ",
    suspendEngine: "ระงับเอนจิ้น",
    activateEngine: "เปิดใช้งานเอนจิ้น",
    allResellersAffected: "ตัวแทนจำหน่ายทั้งหมดภายใต้จะได้รับผลกระทบ",
    deleteEngine: "ลบเอนจิ้น",
    editEngine: "แก้ไขเอนจิ้น",
    createNewEngine: "สร้างเอนจิ้นใหม่",
    updateEngineDetails: "อัปเดตรายละเอียดเอนจิ้น",
    setUpNewEnginePartner: "ตั้งค่าพาร์ทเนอร์เอนจิ้นใหม่",
    generalTab: "ทั่วไป",
    feesLimitsTab: "ค่าธรรมเนียมและวงเงิน",
    permissionsTab: "สิทธิ์",
    apiAccessTab: "การเข้าถึง API",
    generalInformation: "ข้อมูลทั่วไป",
    businessNameRequired: "ชื่อธุรกิจ *",
    engineCode: "รหัสเอนจิ้น",
    loginEmailRequired: "อีเมลเข้าสู่ระบบ *",
    newPasswordOptional: "รหัสผ่านใหม่ (ไม่บังคับ)",
    passwordRequired: "รหัสผ่าน *",
    leaveBlankToKeepCurrent: "เว้นว่างเพื่อคงค่าเดิม",
    enterPassword: "กรอกรหัสผ่าน",
    contactName: "ชื่อผู้ติดต่อ",
    contactPhone: "เบอร์โทรผู้ติดต่อ",
    supportEmail: "อีเมลฝ่ายสนับสนุน",
    notes: "หมายเหตุ",
    configureFeeStructureLimits: "กำหนดโครงสร้างค่าธรรมเนียมและวงเงิน",
    depositFeeThb: "ค่าธรรมเนียมฝากเงิน (RS)",
    withdrawalFeeThb: "ค่าธรรมเนียมถอนเงิน (RS)",
    percentageFeePercent: "ค่าธรรมเนียมแบบเปอร์เซ็นต์ (%)",
    commissionRatePercent: "อัตราคอมมิชชั่น (%)",
    maxResellers: "จำนวนตัวแทนจำหน่ายสูงสุด",
    maxMerchants: "จำนวนร้านค้าสูงสุด",
    allowedPaymentProviders: "ผู้ให้บริการชำระเงินที่อนุญาต",
    controlEngineActions: "กำหนดสิทธิ์การดำเนินการของเอนจิ้น",
    canCreateResellers: "สามารถสร้างตัวแทนจำหน่าย",
    allowCreatingNewResellerAccounts: "อนุญาตให้สร้างบัญชีตัวแทนจำหน่ายใหม่",
    canCreateMerchants: "สามารถสร้างร้านค้า",
    canCreateMerchantsDirect: "สามารถสร้างร้านค้า (โดยตรง)",
    allowCreatingMerchantAccountsDirectly: "อนุญาตให้สร้างร้านค้าโดยตรง",
    canEditFeeStructure: "สามารถแก้ไขโครงสร้างค่าธรรมเนียม",
    allowModifyingFeeSettings: "อนุญาตให้แก้ไขการตั้งค่าค่าธรรมเนียม",
    canViewWebhookLogs: "สามารถดูบันทึกเว็บฮุค",
    accessToRawWebhookData: "เข้าถึงข้อมูลเว็บฮุคแบบดิบ",
    canRegenerateApiKeys: "สามารถสร้างคีย์ API ใหม่",
    allowRegeneratingApiCredentials: "อนุญาตให้สร้างข้อมูลรับรอง API ใหม่",
    apiCredentials: "ข้อมูลรับรอง API",
    shareCredentialsSecurely: "แชร์ข้อมูลรับรองอย่างปลอดภัยกับพาร์ทเนอร์เอนจิ้น",
    keepCredentialsSecure: "เก็บข้อมูลรับรองอย่างปลอดภัย เนื่องจากมีสิทธิ์เข้าถึง API เต็มรูปแบบ",
    updateEngine: "อัปเดตเอนจิ้น",
    pleaseFillAllRequiredFields: "โปรดกรอกข้อมูลที่จำเป็นทั้งหมด",
    engineCodePlaceholder: "ENG001",
    businessNamePlaceholder: "Gaming Partners Ltd",
    loginEmailPlaceholder: "admin@engine.com",

    // Resellers
    resellerPartners: "พาร์ทเนอร์เอนจิ้นที่ลงทะเบียน",
    merchantsUnderThisReseller: "ร้านค้าที่อยู่ภายใต้ตัวแทนจำหน่ายนี้",

    searchResellers: "ค้นหาตัวแทนจำหน่าย...",
    createReseller: "สร้างตัวแทนจำหน่าย",
    reseller: "ตัวแทนจำหน่าย",
    monthlyVolume: "ปริมาณรายเดือน",
    suspendReseller: "ระงับตัวแทนจำหน่าย",
    activateReseller: "เปิดใช้งานตัวแทนจำหน่าย",
    merchantsWillBeAffected: "พวกเขาและร้านค้าของพวกเขาจะไม่สามารถทำธุรกรรมได้",
    deleteReseller: "ลบตัวแทนจำหน่าย",
    merchantsBecomeOrphaned: "ร้านค้าทั้งหมดภายใต้ตัวแทนจำหน่ายนี้จะกลายเป็นไม่มีต้นสังกัด",
    editReseller: "แก้ไขตัวแทนจำหน่าย",
    createNewReseller: "สร้างตัวแทนจำหน่ายใหม่",
    updateResellerDetails: "อัปเดตรายละเอียดและสิทธิ์ของตัวแทนจำหน่าย",
    setUpNewResellerPartner: "ตั้งค่าพาร์ทเนอร์ตัวแทนจำหน่ายใหม่",
    resellerProfileAndContact: "โปรไฟล์และข้อมูลติดต่อของตัวแทนจำหน่าย",
    resellerCode: "รหัสตัวแทนจำหน่าย",
    parentType: "ประเภทต้นสังกัด",
    whoManagesThisReseller: "ใครเป็นผู้ดูแลตัวแทนจำหน่ายนี้",
    emailCannotBeChangedAfterCreation: "ไม่สามารถเปลี่ยนอีเมลหลังจากสร้างแล้ว",
    newPasswordLeaveBlank: "รหัสผ่านใหม่ (เว้นว่างเพื่อคงค่าเดิม)",
    configureResellerFeeStructure: "กำหนดโครงสร้างค่าธรรมเนียมและวงเงินของตัวแทนจำหน่าย",
    maxMerchantsLabel: "จำนวนร้านค้าสูงสุด",
    maximumMerchantsResellerCanCreate: "จำนวนร้านค้าสูงสุดที่ตัวแทนจำหน่ายสร้างได้",
    resellerCommissionOnTransactions: "คอมมิชชั่นของตัวแทนจำหน่ายต่อธุรกรรม",
    thisCommissionRateSetBy: "อัตราคอมมิชชั่นนี้กำหนดโดย {name} และไม่สามารถเปลี่ยนได้",
    allowedProviders: "ผู้ให้บริการที่อนุญาต",
    selectWhichPaymentProviders: "เลือกผู้ให้บริการชำระเงินที่ตัวแทนจำหน่ายสามารถใช้ได้",
    routingOverride: "การกำหนดเส้นทางแทนที่",
    configureRoutingBehavior: "กำหนดพฤติกรรมการกำหนดเส้นทางสำหรับตัวแทนจำหน่าย",
    failoverEnabled: "เปิดใช้งานการสลับสำรอง",
    automaticallyTryNextProvider: "ลองผู้ให้บริการถัดไปโดยอัตโนมัติเมื่อเกิดความล้มเหลว",
    priorityProvidersInOrder: "ลำดับผู้ให้บริการตามความสำคัญ",
    featurePermissions: "สิทธิ์ฟีเจอร์",
    controlWhatActionsReseller: "กำหนดสิทธิ์การดำเนินการของตัวแทนจำหน่าย",
    visibleDashboardPages: "หน้าที่มองเห็นได้ในแดชบอร์ด",
    controlWhichPagesReseller: "กำหนดหน้าที่ตัวแทนจำหน่ายสามารถเข้าถึงได้",
    resellerApiKeysForIntegration: "คีย์ API ของตัวแทนจำหน่ายสำหรับการเชื่อมต่อ",
    apiKeysAutoGenerated: "คีย์ API ถูกสร้างอัตโนมัติ โปรดแชร์อย่างปลอดภัย",
    operationalLimits: "ขีดจำกัดการดำเนินงาน",
    manageResellerCapabilitiesAndLimits: "จัดการความสามารถและขีดจำกัดของตัวแทนจำหน่าย",
    updateReseller: "อัปเดตตัวแทนจำหน่าย",
    createResellerButton: "สร้างตัวแทนจำหน่าย",

    // Servers
    serverInfrastructure: "โครงสร้างพื้นฐานเซิร์ฟเวอร์",
    realTimeMonitoring: "การเฝ้าระวังแบบเรียลไทม์และสถานะระบบ",
    last1h: "1 ชม.ล่าสุด",
    last6h: "6 ชม.ล่าสุด",
    last24h: "24 ชม.ล่าสุด",
    last7d: "7 วันล่าสุด",
    healthy: "ปกติ",
    warnings: "คำเตือน",
    avgCpu: "CPU เฉลี่ย",
    avgMemory: "หน่วยความจำเฉลี่ย",
    requestsPerMin: "คำขอต่อนาที",
    avgLatency: "เวลาแฝงเฉลี่ย",
    overview: "ภาพรวม",
    performance: "ประสิทธิภาพ",
    network: "เครือข่าย",
    cpuUsage: "การใช้งาน CPU (24 ชม.)",
    memoryUsage: "การใช้งานหน่วยความจำ (24 ชม.)",
    responseLatency: "เวลาแฝงการตอบกลับ (24 ชม.)",
    networkTraffic: "ปริมาณข้อมูลเครือข่ายตามเซิร์ฟเวอร์",
    cpu: "CPU",
    memory: "หน่วยความจำ",
    disk: "ดิสก์",
    latency: "เวลาแฝง",
    uptime: "เวลาเปิดใช้งาน",
    reqPerMin: "คำขอต่อนาที",
    viewLogs: "ดูบันทึก",
    requestsPerMin24h: "คำขอต่อนาที (24 ชม.)",
    disabled: "ปิดใช้งาน",
    warning: "คำเตือน",
    critical: "วิกฤต",
    offline: "ออฟไลน์",
    serverLogs: "บันทึกเซิร์ฟเวอร์",
    serverLogsTitle: "บันทึกเซิร์ฟเวอร์ - {name}",
    viewServerLogsDescription: "ดูบันทึกเซิร์ฟเวอร์ รวมถึงเวลาเริ่มต้น การเชื่อมต่อฐานข้อมูล การใช้หน่วยความจำ และสถานะคำขอ",
    loadingLogs: "กำลังโหลดบันทึก...",
    serverStarted: "เซิร์ฟเวอร์เริ่มทำงาน",
    databaseConnectionEstablished: "เชื่อมต่อฐานข้อมูลแล้ว",
    currentMemoryUsage: "การใช้หน่วยความจำปัจจุบัน",
    totalLogEntries: "จำนวนบันทึกทั้งหมด",
    filterByStatus: "กรองตามสถานะ",
    allStatuses: "ทุกสถานะ",
    statusUp: "UP",
    statusLow: "LOW",
    statusCritical: "CRITICAL",
    statusDown: "DOWN",
    recentLogEntries: "บันทึกล่าสุด",
    showingEntries: "แสดง {filtered} จาก {total} รายการ",
    showingEntriesStatusChanges: "แสดง {filtered} จาก {total} รายการ (เฉพาะการเปลี่ยนสถานะ)",
    latencyLabel: "เวลาแฝง",
    noLogsAvailable: "ไม่มีบันทึก",
    connectionFailed: "การเชื่อมต่อล้มเหลว - คำขออาจถูกจัดคิวหรือไม่สำเร็จ",
    criticalLatencyDetected: "ตรวจพบเวลาแฝงวิกฤต - ต้องดูแลทันที",
    highLatencyDetected: "ตรวจพบเวลาแฝงสูง - ประสิทธิภาพอาจลดลง",
    apiGateway: "เกตเวย์ API",
    bankserver: "เซิร์ฟเวอร์ธนาคาร",
    paymentProcessor: "ตัวประมวลผลการชำระเงิน",
    databasePrimary: "ฐานข้อมูลหลัก",
    databaseReplica: "ฐานข้อมูลสำรอง",
    webhookHandler: "ตัวจัดการเว็บฮุค",
    queueProcessor: "ตัวประมวลผลคิว",
    loadBalancer: "ตัวกระจายโหลด",
    redisCache: "แคช Redis",
    singapore: "สิงคโปร์",
    bangkok: "กรุงเทพ",
    na: "ไม่ระบุ",
    failedToFetchLogs: "ไม่สามารถดึงบันทึกได้",
    performanceCPUGraph: "CPU",
    performanceMemoryGraph: "หน่วยความจำ",
    performanceRequestsGraph: "คำขอ",
    performanceLatencyGraph: "เวลาแฝง",

    // Merchant Panel
    merchantPortal: "พอร์ทัลร้านค้า",
    navigation: "การนำทาง",
    loggedInAs: "เข้าสู่ระบบในชื่อ",

    // Merchant Dashboard

    // Merchant Settings
    apiKeys: "คีย์ API",
    yourAuthCredentials: "ข้อมูลรับรองสำหรับการเข้าถึง API ของคุณ",
    apiKeyPublic: "คีย์ API (สาธารณะ)",
    secretKey: "คีย์ลับ",
    regenerateKeys: "สร้างคีย์ใหม่",
    invalidateCurrentKeys: "การดำเนินการนี้จะยกเลิกคีย์ปัจจุบัน",
    regenerate: "สร้างใหม่",
    regenerateApiKeys: "สร้างคีย์ API ใหม่?",
    invalidateImmediately: "การดำเนินการนี้จะยกเลิกคีย์ปัจจุบันทันที การเชื่อมต่อเดิมทั้งหมดจะหยุดทำงานจนกว่าจะอัปเดต",
    callbackUrls: "Callback URLs",
    whereWeSendNotifications: "ตำแหน่งที่ส่งการแจ้งเตือนการชำระเงิน",
    callbackUrl: "URL เรียกกลับ",
    wePostToEndpoint: "ระบบจะส่งข้อมูลธุรกรรมไปยังปลายทางนี้",
    webhookUrl: "URL เว็บฮุค (ไม่บังคับ)",
    additionalEventNotifications: "สำหรับการแจ้งเตือนเหตุการณ์เพิ่มเติม",
    saveUrls: "บันทึก URL",
    merchantProfile: "โปรไฟล์ร้านค้า",
    yourBusinessInfo: "ข้อมูลธุรกิจของคุณ",
    businessName: "ชื่อธุรกิจ",
    emailAddress: "ที่อยู่อีเมล",
    supportContact: "ผู้ติดต่อฝ่ายสนับสนุน",
    saveProfile: "บันทึกโปรไฟล์",

    // Engine Panel
    enginePanel: "พอร์ทัลเอนจิ้น",

    // Reseller Panel
    resellerPanel: "พอร์ทัลตัวแทนจำหน่าย",

    // Common form labels
    name: "ชื่อ",
    email: "อีเมล",
    phone: "โทรศัพท์",
    contactEmail: "อีเมลผู้ติดต่อ",
    keepSecretConfidential: "โปรดเก็บคีย์ลับของคุณเป็นความลับ",
    securityNotice: "ประกาศด้านความปลอดภัย",
    neverExposeSecretKey: "ห้ามเปิดเผยคีย์ลับในฝั่งไคลเอนต์หรือเผยแพร่สาธารณะ",
    regenerateAllKeys: "สร้างคีย์ใหม่ทั้งหมด",
    invalidateCurrentKeysWarning: "สร้างคีย์ API ใหม่ คีย์ปัจจุบันจะหยุดใช้งานทันที",

    // Withdrawals
    manualPayout: "จ่ายเงินด้วยตนเอง",
    newWithdrawal: "คำขอถอนเงินใหม่",
    history: "ประวัติ",
    withdrawalRequestSubmittedSuccessfully: "ส่งคำขอถอนเงินสำเร็จ!",
    insufficientBalance: "ยอดเงินคงเหลือไม่เพียงพอ",
    invalidAmount: "จำนวนเงินไม่ถูกต้อง",
    invalidWalletAddress: "ที่อยู่กระเป๋าเงินไม่ถูกต้อง",
    invalidBankAccount: "รายละเอียดบัญชีธนาคารไม่ถูกต้อง",
    unauthorizedRequest: "ไม่มีสิทธิ์ในการดำเนินการ",
    forbiddenRequest: "ไม่อนุญาตให้เข้าถึง",
    somethingWentWrong: "เกิดข้อผิดพลาดบางอย่าง",
    noWithdrawalsFound: "ไม่พบรายการถอนเงิน",
    bankAccountNumber: "เลขบัญชีธนาคาร",
    accountHolderName: "ชื่อผู้ถือบัญชี",
    enterAccountHolderName: "กรอกชื่อผู้ถือบัญชี",
    orderId: "รหัสคำสั่งซื้อ",
    customerId: "รหัสลูกค้า",
    optionalCustomerRef: "อ้างอิงลูกค้า (ไม่บังคับ)",
    createPayout: "สร้างการจ่ายเงิน",

    // Dialogs
    areYouSure: "คุณแน่ใจหรือไม่?",

    // Login Forms
    rememberMe: "จดจำฉัน",
    forgotPassword: "ลืมรหัสผ่าน?",
    signIn: "เข้าสู่ระบบ",
    signingIn: "กำลังเข้าสู่ระบบ...",
    signInAsStaff: "เข้าสู่ระบบในฐานะพนักงาน",
    signInAs: "เข้าสู่ระบบในฐานะ {type}",
    signInToPortal: "เข้าสู่ระบบพอร์ทัล",
    selectAccountTypeAndEnterCredentials: "เลือกประเภทบัญชีและกรอกข้อมูลประจำตัว",
    welcomeBackAdmin: "ยินดีต้อนรับกลับ",
    signInToAccessAdminDashboard: "เข้าสู่ระบบเพื่อเข้าถึงแดชบอร์ดผู้ดูแลระบบ",
    signInToYourMerchantAccount: "เข้าสู่ระบบบัญชีร้านค้าของคุณ",
    paymentGatewayAdminPanel: "แผงควบคุมผู้ดูแลระบบ Payment Gateway",
    accessRestrictedToAuthorizedOnly: "การเข้าถึงจำกัดเฉพาะผู้ได้รับอนุญาตเท่านั้น",
    staffMemberClickHereToLogin: "สมาชิกพนักงาน? คลิกที่นี่เพื่อเข้าสู่ระบบ",
    enginePortalAccess: "การเข้าถึงพอร์ทัลเอนจิ้น",
    partnerPortalAccess: "การเข้าถึงพอร์ทัลพันธมิตร",
    staffMemberLogin: "เข้าสู่ระบบสมาชิกพนักงาน",
    staffAccess: "การเข้าถึงพนักงาน",
    signInWithStaffCredentials: "เข้าสู่ระบบด้วยข้อมูลประจำตัวพนักงาน",
    panelType: "ประเภทแผงควบคุม",
    selectYourPanelType: "เลือกประเภทแผงควบคุมของคุณ",
    selectPanelYouWereAssignedTo: "เลือกแผงควบคุมที่คุณได้รับมอบหมาย",
    adminPanel: "แผงควบคุมผู้ดูแลระบบ",
    administrativeStaff: "พนักงานฝ่ายบริหาร",
    engineStaff: "พนักงานเอนจิ้น",
    resellerStaff: "พนักงานตัวแทนจำหน่าย",
    merchantStaff: "พนักงานร้านค้า",
    staffAccessOnly: "เฉพาะพนักงานเท่านั้น ไม่ใช่สมาชิกพนักงาน?",
    adminLogin: "เข้าสู่ระบบผู้ดูแลระบบ",
    engineLogin: "เข้าสู่ระบบเอนจิ้น",
    resellerLogin: "เข้าสู่ระบบตัวแทนจำหน่าย",
    merchantLogin: "เข้าสู่ระบบร้านค้า",
    iAmStaffMember: "ฉันเป็นสมาชิกพนักงาน",
    needHelpContact: "ต้องการความช่วยเหลือ? ติดต่อ",
    needHelpAccessingAccount: "ต้องการความช่วยเหลือในการเข้าถึงบัญชีของคุณ?",
    contactSupport: "ติดต่อฝ่ายสนับสนุน",
    merchantBusinessAccess: "การเข้าถึงธุรกิจร้านค้า",
    resellerPartnerAccess: "การเข้าถึงพันธมิตรตัวแทนจำหน่าย",
    allRightsReserved: "สงวนลิขสิทธิ์",
    privacyPolicy: "นโยบายความเป็นส่วนตัว",
    termsOfService: "เงื่อนไขการให้บริการ",

    // Infrastructure
    infrastructure: "โครงสร้างพื้นฐาน",
    database: "ฐานข้อมูล",
    queue: "คิว",

    // Additional
    total: "รวม",

    // Transaction fields
    transaction: "ธุรกรรม",
    method: "วิธี",
    ref: "อ้างอิง",
    paymentId: "รหัสการชำระเงิน",
    clientName: "ชื่อลูกค้า",
    clientBankAccount: "บัญชีธนาคารลูกค้า",

    // Filters
    dateRange: "ช่วงวันที่",
    clearFilters: "ล้างตัวกรอง",
    exportCsv: "ส่งออก CSV",
    searchHeaderPlaceholder: "ค้นหาธุรกรรม ร้านค้า...",

    // Stats
    vsLastPeriod: "เทียบกับช่วงก่อนหน้า",
    live: "เรียลไทม์",
    noTransactionsYet: "ยังไม่มีธุรกรรม",
    noProvidersConfigured: "ยังไม่ได้กำหนดผู้ให้บริการ",
    noProvidersFound: "ไม่พบผู้ให้บริการ",
    avgResponse: "เวลาเฉลี่ยในการตอบกลับ",

    // Routing Rule Form fields

    // Withdrawal form
    selectMerchant: "เลือกร้านค้า",
    selectBank: "เลือกธนาคาร",

    // Priority badge text
    priorityText: "ลำดับความสำคัญ",
    viewOnlyBadge: "ดู",

    // Additional transaction/common
    retry: "ลองใหม่",
    noTransactionsFound: "ไม่พบธุรกรรม",
    noDeposits: "ไม่พบรายการฝากเงิน",

    // Transaction Detail
    transactionDetails: "รายละเอียดธุรกรรม",
    transactionInfo: "ข้อมูลธุรกรรม",
    processingInfo: "ข้อมูลการประมวลผล",
    customerInfo: "ข้อมูลลูกค้า",
    bankDetails: "รายละเอียดธนาคาร",
    callbackStatus: "สถานะคอลแบ็ก",
    timestamps: "เวลา",
    retryTransaction: "ลองทำธุรกรรมอีกครั้ง",
    resendCallback: "ส่งคอลแบ็กอีกครั้ง",
    routingAttempts: "จำนวนครั้งการกำหนดเส้นทาง",
    attempts: "จำนวนครั้ง",
    lastAttempt: "ครั้งล่าสุด",
    createdAt: "สร้างเมื่อ",
    processedAt: "ประมวลผลเมื่อ",
    completedAt: "เสร็จสิ้นเมื่อ",
    updatedAt: "อัปเดตเมื่อ",
    currency: "สกุลเงิน",
    feeAmount: "ค่าธรรมเนียม",
    providerRef: "อ้างอิงผู้ให้บริการ",
    account: "บัญชี",
    nA: "ไม่ระบุ",
    errorCode: "รหัสข้อผิดพลาด",
    qrCode: "คิวอาร์โค้ด",
    qrPayment: "การชำระเงินด้วยคิวอาร์",
    qrPaymentInfo: "ข้อมูลการชำระเงินคิวอาร์",
    qrCodeInfo: "ข้อมูลคิวอาร์โค้ด",
    qrCodeDetails: "รายละเอียดคิวอาร์โค้ด",
    qrCodePayment: "การชำระเงินคิวอาร์โค้ด",
    expiryInfo: "ข้อมูลวันหมดอายุ",
    expiresAt: "หมดอายุเมื่อ",
    viewQrCode: "ดูคิวอาร์โค้ด",
    expires: "หมดอายุ",
    manualStatusUpdate: "อัปเดตสถานะด้วยตนเอง",
    updateStatus: "อัปเดตสถานะ",
    newStatus: "สถานะใหม่",
    adminNoteOptional: "บันทึกผู้ดูแลระบบ (ไม่บังคับ)",
    adminNote: "บันทึกผู้ดูแลระบบ",
    reasonForStatusChange: "เหตุผลในการเปลี่ยนสถานะ...",
    characters: "ตัวอักษร",
    updating: "กำลังอัปเดต...",
    statusUpdated: "อัปเดตสถานะแล้ว",
    transactionStatusChangedTo: "สถานะธุรกรรมถูกเปลี่ยนเป็น",
    updateFailed: "อัปเดตไม่สำเร็จ",
    failedToUpdateTransactionStatus: "ไม่สามารถอัปเดตสถานะธุรกรรมได้",
    webhookInfo: "ข้อมูลเว็บฮุค",
    webhook: "เว็บฮุค",
    url: "URL",
    rawWebhookData: "ข้อมูลเว็บฮุคดิบ",
    updated: "อัปเดตแล้ว",
    ip: "IP",
    hierarchy: "ลำดับชั้น",
    delivered: "ส่งแล้ว",
    callback: "คอลแบ็ก",

    // Withdrawal Form
    bankTransfer: "โอนผ่านธนาคาร",
    usdtCrypto: "USDT คริปโต",
    selectBankLabel: "เลือกธนาคาร",
    chooseYourBank: "เลือกธนาคารของคุณ",
    accountNumber: "เลขบัญชี",
    enterAccountNumber: "กรอกเลขบัญชีธนาคาร",
    amountThb: "จำนวนเงิน (PKR)",
    minimumWithdrawal: "ถอนขั้นต่ำ",
    withdrawToBank: "ถอนเข้าธนาคาร",
    networkLabel: "เครือข่าย",
    walletAddress: "ที่อยู่กระเป๋าเงิน",
    enterWalletAddress: "กรอกที่อยู่ {network} USDT ของคุณ",
    amountUsdt: "จำนวนเงิน (USDT)",
    thbEquivalent: "มูลค่าเทียบเท่า PKR",
    exchangeRate: "อัตราแลกเปลี่ยน",
    withdrawUsdt: "ถอน USDT",
    withdrawalSummary: "สรุปการถอน",
    totalWithdrawn: "ยอดถอนรวม",
    requests: "คำขอ",
    processingTime: "เวลาประมวลผล",
    hours: "ชั่วโมง",
    mins: "นาที",
    needHelp: "ต้องการความช่วยเหลือ?",
    walletWarning: "โปรดตรวจสอบให้แน่ใจว่าที่อยู่กระเป๋าเงินถูกต้องและรองรับ",
    incorrectAddressWarning: "ที่อยู่ที่ไม่ถูกต้องอาจทำให้สูญเสียเงินอย่างถาวร",

    // Merchant Form
    editMerchant: "แก้ไขร้านค้า",
    addNewMerchant: "เพิ่มร้านค้าใหม่",
    merchantName: "ชื่อร้านค้า",
    merchantCode: "รหัสร้านค้า",
    merchantInfo: "ข้อมูลร้านค้า",
    directAdmin: "โดยตรง (แอดมิน)",
    enableDeposits: "เปิดใช้งานการฝากเงิน",
    allowDepositTransactions: "อนุญาตให้ทำธุรกรรมฝากเงิน",
    enableWithdrawals: "เปิดใช้งานการถอนเงิน",
    allowWithdrawalTransactions: "อนุญาตให้ทำธุรกรรมถอนเงิน",
    apiSettings: "การตั้งค่า API",
    limitsFees: "วงเงินและค่าธรรมเนียม",
    contact: "ผู้ติดต่อ",
    securityNote: "คีย์ API แสดงได้เพียงครั้งเดียว โปรดจัดเก็บอย่างปลอดภัย",
    ipWhitelist: "รายการ IP ที่อนุญาต",
    email: "อีเมล",
    noIpsWhitelisted: "ไม่มี IP ที่อนุญาต",
    rateLimit: "จำกัดอัตรา (คำขอต่อนาที)",
    minDeposit: "ฝากขั้นต่ำ",
    maxDeposit: "ฝากสูงสุด",
    minWithdrawal: "ถอนขั้นต่ำ",
    maxWithdrawal: "ถอนสูงสุด",
    dailyDepositLimit: "วงเงินฝากรายวัน",
    dailyWithdrawalLimit: "วงเงินถอนรายวัน",
    feeType: "ประเภทค่าธรรมเนียม",
    feeValue: "มูลค่าค่าธรรมเนียม",
    percentage: "เปอร์เซ็นต์",
    fixed: "คงที่",
    tiered: "ขั้นบันได",
    settlementPeriod: "รอบชำระบัญชี",
    sameDay: "วันเดียวกัน",
    nextDay: "วันถัดไป",
    weekly: "รายสัปดาห์",
    updateMerchant: "อัปเดตร้านค้า",
    createMerchantBtn: "สร้างร้านค้า",
    emailCannotBeChanged: "ไม่สามารถเปลี่ยนอีเมลได้",
    password: "รหัสผ่าน",
    changePassword: "เปลี่ยนรหัสผ่าน",
    leaveBlankToKeepPassword: "เว้นว่างเพื่อคงรหัสผ่านเดิม",
    changePasswordDescription: "กรอกรหัสผ่านใหม่เพื่อเปลี่ยน หรือเว้นว่างเพื่อคงเดิม",
    selectEngine: "เลือกเอนจิ้น",
    selectReseller: "เลือกตัวแทนจำหน่าย",
    whoManagesMerchant: "ใครเป็นผู้ดูแลร้านค้านี้",
    internalNotes: "บันทึกภายใน...",
    apiKeysShownOnce: "คีย์ API แสดงได้เพียงครั้งเดียว โปรดจัดเก็บอย่างปลอดภัย",
    ipWhitelistPlaceholder: "เช่น 192.168.1.1",
    minDepositThb: "ฝากขั้นต่ำ (PKR)",
    maxDepositThb: "ฝากสูงสุด (PKR)",
    minWithdrawalThb: "ถอนขั้นต่ำ (PKR)",
    maxWithdrawalThb: "ถอนสูงสุด (PKR)",
    dailyDepositLimitThb: "วงเงินฝากรายวัน (PKR)",
    dailyWithdrawalLimitThb: "วงเงินถอนรายวัน (PKR)",
    feeValueLabel: "มูลค่าค่าธรรมเนียม",
    backButton: "ย้อนกลับ",
    nextButton: "ถัดไป",
    merchantNamePlaceholder: "เช่น Gaming Site A",
    merchantCodePlaceholder: "เช่น MGC001",
    emailPlaceholder: "merchant@example.com",
    passwordPlaceholder: "กรอกรหัสผ่าน",
    callbackUrlPlaceholder: "https://your-site.com/api/payment/callback",
    webhookUrlPlaceholder: "https://your-site.com/api/payment/webhook",
    contactNamePlaceholder: "John Doe",
    contactEmailPlaceholder: "john@example.com",
    contactPhonePlaceholder: "+66 XXX XXX XXXX",
    t0SameDay: "T+0 (วันเดียวกัน)",
    t1NextDay: "T+1 (วันถัดไป)",
    t3: "T+3",
    t7Weekly: "T+7 (รายสัปดาห์)",
    createMerchantUnderReseller: "สร้างร้านค้าใหม่ภายใต้บัญชีตัวแทนจำหน่ายของคุณ",
    commissionGivenToYouBy: "อัตราคอมมิชชั่นนี้มอบให้คุณโดย {name} คุณสามารถกำหนดคอมมิชชั่นให้ร้านค้านี้ได้",
    feeValueSetToBy: "มูลค่าค่าธรรมเนียมนี้ตั้งไว้ที่ {value}% (อัตราคอมมิชชั่นที่มอบให้โดย {name}) ไม่สามารถเปลี่ยนแปลงได้",
    apisettingsapiKeyPlaceholder: "เว้นว่างเพื่อคงคีย์ API เดิม",
    apisettingssecretKeyPlaceholder: "เว้นว่างเพื่อคงคีย์ลับเดิม",

    // Provider Form
    editProvider: "แก้ไขผู้ให้บริการ",
    addNewProvider: "เพิ่มผู้ให้บริการใหม่",
    providerNameRequired: "ชื่อผู้ให้บริการ *",
    providerCodeRequired: "รหัสผู้ให้บริการ *",
    providerNamePlaceholder: "เช่น SCB",
    providerCodePlaceholder: "เช่น SCB",
    providerTypeEwallet: "อีวอลเล็ต",
    providerTypeQr: "ชำระเงินด้วย QR",
    providerTypeCard: "บัตร",
    providerName: "ชื่อผู้ให้บริการ",
    providerCode: "รหัสผู้ให้บริการ",
    supportsDeposits: "รองรับการฝากเงิน",
    processDepositTransactions: "ประมวลผลธุรกรรมฝากเงิน",
    supportsWithdrawals: "รองรับการถอนเงิน",
    processWithdrawalTransactions: "ประมวลผลธุรกรรมถอนเงิน",
    supportsQr: "รองรับการชำระเงินด้วย QR",
    generatePromptPayQr: "สร้าง QR PromptPay",
    logoUrl: "URL โลโก้",
    logoUrlPlaceholder: "https://...",
    apiConfig: "การตั้งค่า API",
    routingLimits: "การกำหนดเส้นทางและวงเงิน",
    apiEndpoint: "ปลายทาง API",
    apiEndpointPlaceholder: "https://api.provider.com/v1",
    apiKey: "คีย์ API",
    apiSecret: "คีย์ลับ API",
    webhookSecret: "คีย์ลับเว็บฮุค",
    providerIpWhitelist: "รายการ IP ผู้ให้บริการ (สำหรับเว็บฮุค)",
    priorityHighest: "ลำดับความสำคัญ (1 = สูงสุด)",
    weightLoadBalancing: "น้ำหนัก (กระจายโหลด)",
    dailyLimit: "วงเงินรายวัน",
    minAmountThb: "จำนวนขั้นต่ำ (PKR)",
    maxAmountThb: "จำนวนสูงสุด (PKR)",
    dailyLimitThb: "วงเงินรายวัน (PKR)",
    feePercentage: "ค่าธรรมเนียมเปอร์เซ็นต์",
    settlementTimeLabel: "เวลาชำระบัญชี",
    instant: "ทันที",
    oneHour: "1 ชั่วโมง",
    updateProvider: "อัปเดตผู้ให้บริการ",
    addProviderBtn: "เพิ่มผู้ให้บริการ",

    // Engine Panel specific
    totalVolume: "ปริมาณรวม",
    successRate: "อัตราความสำเร็จ",
    totalWithdrawals: "ยอดถอนทั้งหมด",
    totalDeposits: "ยอดฝากทั้งหมด",
    totalTransactions: "ธุรกรรมทั้งหมด",
    providerDistribution: "สัดส่วนผู้ให้บริการ",
    recent20Transactions: "20 ธุรกรรมล่าสุด",
    searchByIdOrReference: "ค้นหาด้วยรหัสหรือเลขอ้างอิง...",
    resellersCreatedByThisEngine: "ตัวแทนจำหน่ายที่สร้างโดยเอนจิ้นนี้",
    loginLogs: "บันทึกการเข้าสู่ระบบ",
    apiUsageLogs: "บันทึกการใช้งาน API",
    additionalSettings: "การตั้งค่าเพิ่มเติม",
    operationalLimitsAndPaymentMethods: "ข้อจำกัดการดำเนินงานและวิธีชำระเงิน",
    configureDepositAndWithdrawalFeesForThisEngine: "กำหนดค่าธรรมเนียมฝากและถอนสำหรับเอนจิ้นนี้",
    configureDepositAndWithdrawalFees: "กำหนดค่าธรรมเนียมฝากและถอนพร้อมอัตราคอมมิชชั่น",
    depositAndWithdrawal: "ฝากและถอน",
    commission: "คอมมิชชั่น",
    constraints: "ข้อจำกัด",
    depositAndWithdrawalFees: "ค่าธรรมเนียมฝากและถอน",
    depositFee: "ค่าธรรมเนียมฝาก",
    withdrawalFee: "ค่าธรรมเนียมถอน",
    fixedAmount: "จำนวนคงที่",
    commissionRates: "อัตราคอมมิชชั่น",
    commissionEarnedFromTransactions: "คอมมิชชั่นที่ได้รับจากธุรกรรม",
    calculateFeePreview: "คำนวณตัวอย่างค่าธรรมเนียม",
    calculating: "กำลังคำนวณ...",
    feeBreakdownFor1000: "สรุปค่าธรรมเนียมสำหรับ 1000RS:",
    adminFee: "ค่าธรรมเนียมแอดมิน",
    feeConstraints: "ข้อจำกัดค่าธรรมเนียม",
    setMinMaxLimitsForChildEntities: "กำหนดขีดจำกัดต่ำสุด/สูงสุดสำหรับหน่วยย่อย",
    depositConstraints: "ข้อจำกัดการฝาก",
    withdrawalConstraints: "ข้อจำกัดการถอน",
    commissionConstraints: "ข้อจำกัดคอมมิชชั่น",
    minFee: "ค่าธรรมเนียมขั้นต่ำ",
    maxFee: "ค่าธรรมเนียมสูงสุด",
    minDepositCommission: "คอมมิชชั่นฝากขั้นต่ำ",
    maxDepositCommission: "คอมมิชชั่นฝากสูงสุด",
    minWithdrawalCommission: "คอมมิชชั่นถอนขั้นต่ำ",
    maxWithdrawalCommission: "คอมมิชชั่นถอนสูงสุด",
    none: "ไม่มี",
    last7DaysVolume: "ปริมาณธุรกรรม 7 วันล่าสุด",
    recent: "ล่าสุด",
    noMerchantsMatchSearch: "ไม่พบร้านค้าที่ตรงกับการค้นหา",
    todaysVolume: "ปริมาณวันนี้",
    volumeTrend7Days: "แนวโน้มปริมาณ (7 วัน)",
    apiAndSettings: "API และการตั้งค่า",
    avgDeposit: "ยอดฝากรวม",
    searchMerchantsPlaceholder: "ค้นหาร้านค้า...",
    merchantsRegisteredCount: "ร้านค้าที่ลงทะเบียน",
    partnersCount: "พาร์ทเนอร์",
    addReseller: "เพิ่มตัวแทนจำหน่าย",
    noResellers: "ไม่มีตัวแทนจำหน่าย",
    viewOnly: "ดูอย่างเดียว",
    paymentProviderStatus: "สถานะผู้ให้บริการชำระเงิน (จัดการโดยแอดมิน)",
    routingExplanation: "กฎจะถูกประเมินตามลำดับความสำคัญ กฎแรกที่ตรงเงื่อนไขจะกำหนดผู้ให้บริการ หากผู้ให้บริการล้มเหลว ระบบจะสลับไปยังรายถัดไปโดยอัตโนมัติ",
    createFirstRoutingRule: "สร้างกฎแรกเพื่อเริ่มกำหนดเส้นทางธุรกรรม",
    logs: "บันทึก",
    noLogs: "ไม่มีบันทึก",
    payload: "เพย์โหลด",
    noSettlementsFound: "ไม่พบการชำระบัญชี",
    requestPayoutsViaBankUsdt: "ขอรับการจ่ายผ่านธนาคารหรือ USDT",
    clear: "ล้าง",
    feeConfiguration: "การกำหนดค่าธรรมเนียม",
    percentageFeeLabel: "ค่าธรรมเนียมเปอร์เซ็นต์ (%)",
    commissionRatesAndFeeInfo: "อัตราคอมมิชชั่นและข้อมูลค่าธรรมเนียม",
    ratesAssignedToYou: "นี่คืออัตราที่กำหนดให้คุณ",
    assignedBy: "กำหนดโดย",
    createNewMerchant: "สร้างร้านค้าใหม่",
    businessNameLabel: "ชื่อธุรกิจ",
    emailLabel: "อีเมล",
    callbackUrlLabel: "Callback URL",
    newDeposit: "ฝากเงินใหม่",
    generateQrDeposit: "สร้าง QR สำหรับฝาก",
    createPromptPayQr: "สร้าง QR PromptPay สำหรับลูกค้า",
    howItWorks: "วิธีการทำงาน",
    enterDepositAmount: "กรอกจำนวนเงินฝาก",
    generateQr: "สร้างคิวอาร์โค้ด",
    customerScansQr: "ลูกค้าสแกนคิวอาร์",
    paymentProcessed: "ประมวลผลการชำระเงินแล้ว",
    waitingForPayment: "รอการชำระเงิน",
    statusAutoRefresh: "สถานะจะรีเฟรชอัตโนมัติ",
    paymentReceived: "ได้รับการชำระเงิน",
    qrCodeExpired: "คิวอาร์โค้ดหมดอายุ",
    generateNewQr: "สร้างคิวอาร์ใหม่",

    // Transaction Detail Sheet
    statusTimeline: "ไทม์ไลน์สถานะ",
    createdLabel: "สร้างแล้ว",
    processingLabel: "กำลังประมวลผล",
    completedLabel: "เสร็จสิ้น",
    failedLabel: "ล้มเหลว",
    error: "ข้อผิดพลาด",
    reference: "อ้างอิง",

    // Settlement Page
    pendingSettlement: "รอชำระบัญชี",
    totalRecords: "จำนวนรายการทั้งหมด",
    transactionVolume: "ปริมาณธุรกรรม",
    last7DaysPerformance: "ประสิทธิภาพ 7 วันล่าสุด",
    transactionPerformance: "ประสิทธิภาพธุรกรรม",
    welcomeBackOverview: "ยินดีต้อนรับกลับ! นี่คือภาพรวมของคุณ",
    noEmail: "ไม่มีอีเมล",

    // Additional missing translations
    searchByWebhookId: "ค้นหาตามรหัสเว็บฮุค รหัสธุรกรรม แหล่งที่มา...",
    selectPanelType: "เลือกประเภทแผงควบคุมของคุณ",
    enterNewPassword: "กรอกรหัสผ่านใหม่",
    searchRoles: "ค้นหาบทบาท...",
    searchStaff: "ค้นหาสมาชิกพนักงาน...",
    searchBySettlementId: "ค้นหาตามรหัสการชำระบัญชี...",
    searchBySettlementIdPlaceholder: "ค้นหาตามรหัสการชำระบัญชี...",
    searchByWithdrawalIdAccount: "ค้นหาตามรหัสการถอนหรือบัญชี...",
    searchByTransactionId: "ค้นหาตามรหัสธุรกรรม...",
    defaultQrRoute: "เช่น เส้นทาง QR เริ่มต้น",
    describeRole: "อธิบายบทบาทนี้ว่าสามารถทำอะไรได้บ้าง...",
    orderIdPlaceholder: "รหัสคำสั่งซื้อ, รหัสลูกค้า, ฯลฯ",
    amountPlaceholder: "0.00",
    accountNumberPlaceholder: "เช่น 1234567890",
    accountHolderPlaceholder: "ชื่อเต็มตามบัญชีธนาคาร",
    phonePlaceholder: "+66 XXX XXX XXXX",
    orderIdLabel: "รหัสคำสั่งซื้อ",
    customerIdLabel: "รหัสลูกค้า",
    customerIdPlaceholder: "กรอกรหัสลูกค้า",

    // Admin Roles
    staffRoles: "บทบาทพนักงาน",
    rolesCount: "บทบาท",
    customCount: "กำหนดเอง",
    systemCount: "ระบบ",
    createRole: "สร้างบทบาท",
    editRole: "แก้ไขบทบาท",
    updateRoleDetailsAndPermissions: "อัปเดตรายละเอียดและสิทธิ์ของบทบาท",
    defineNewRoleWithPermissions: "กำหนดบทบาทใหม่พร้อมสิทธิ์เฉพาะ",
    basicDetailsForRole: "รายละเอียดพื้นฐานของบทบาท",
    roleNameRequired: "ชื่อบทบาท *",
    merchantManagerPlaceholder: "เช่น ผู้จัดการร้านค้า",
    describeWhatRoleCanDo: "อธิบายว่าบทบาทนี้ทำอะไรได้บ้าง...",
    roleCreatedForAdminPanel: "บทบาทนี้จะถูกสร้างสำหรับแผงควบคุมผู้ดูแลระบบ",
    selectActionsRoleCanPerform: "เลือกการดำเนินการที่บทบาทสามารถทำได้",
    selectAll: "เลือกทั้งหมด",
    deselectAll: "ยกเลิกการเลือกทั้งหมด",
    noPermissionsSelected: "ยังไม่ได้เลือกสิทธิ์",
    permissionsSelected: "เลือกสิทธิ์แล้ว {count} รายการ",
    permissionSelected: "เลือกสิทธิ์แล้ว 1 รายการ",
    staffWithRoleActions: "สมาชิกพนักงานที่มีบทบาทนี้จะสามารถทำการดำเนินการเหล่านี้ได้",
    updateRole: "อัปเดตบทบาท",
    provideRoleNameAndPermission: "โปรดระบุชื่อบทบาทและเลือกอย่างน้อยหนึ่งสิทธิ์",
    roleEditingNotImplemented: "ยังไม่รองรับการแก้ไขบทบาท โปรดลบแล้วสร้างใหม่",
    searchRolesPlaceholder: "ค้นหาบทบาท...",
    customRoles: "บทบาทกำหนดเอง",
    rolesYouveCreated: "บทบาทที่คุณสร้าง",
    role: "บทบาท",
    permissions: "สิทธิ์",
    noPermissions: "ไม่มีสิทธิ์",
    morePermissions: "เพิ่มเติม",
    systemRoles: "บทบาทระบบ",
    predefinedRolesReadOnly: "บทบาทที่กำหนดไว้ล่วงหน้า (อ่านอย่างเดียว)",
    systemBadge: "ระบบ",
    noRolesFound: "ไม่พบบทบาท",
    tryDifferentSearchTerm: "ลองคำค้นหาอื่น",
    getStartedCreatingFirstRole: "เริ่มต้นด้วยการสร้างบทบาทแรกของคุณ",
    deleteRole: "ลบบทบาท",
    deleteRoleConfirmation: "คุณแน่ใจหรือไม่ว่าต้องการลบบทบาท \"{name}\"? การดำเนินการนี้ไม่สามารถย้อนกลับได้ สมาชิกพนักงานที่มีบทบาทนี้จะสูญเสียสิทธิ์การเข้าถึง",
    systemRolesCannotBeDeleted: "ไม่สามารถลบบทบาทระบบได้",
    panelTypeRequired: "ประเภทแผงควบคุม *",
    permissionsTitle: "สิทธิ์",

    // Admin Roles
    createRoleAction: "สร้างบทบาท",

    // Common

    // Permissions
    "permission.merchants.view": "ดูร้านค้า",
    "permission.merchants.create": "สร้างร้านค้า",
    "permission.merchants.edit": "แก้ไขร้านค้า",
    "permission.merchants.delete": "ลบร้านค้า",
    "permission.admins.view": "ดูผู้ดูแลระบบ",
    "permission.admins.create": "สร้างผู้ดูแลระบบ",
    "permission.admins.edit": "แก้ไขผู้ดูแลระบบ",
    "permission.admins.delete": "ลบผู้ดูแลระบบ",
    "permission.engines.view": "ดูเอนจิ้น",
    "permission.engines.create": "สร้างเอนจิ้น",
    "permission.engines.edit": "แก้ไขเอนจิ้น",
    "permission.engines.delete": "ลบเอนจิ้น",
    "permission.resellers.view": "ดูตัวแทนจำหน่าย",
    "permission.resellers.create": "สร้างตัวแทนจำหน่าย",
    "permission.resellers.edit": "แก้ไขตัวแทนจำหน่าย",
    "permission.resellers.delete": "ลบตัวแทนจำหน่าย",
    "permission.transactions.view": "ดูธุรกรรม",
    "permission.transactions.create": "สร้างธุรกรรม",
    "permission.transactions.edit": "แก้ไขธุรกรรม",
    "permission.staff.view": "ดูพนักงาน",
    "permission.staff.create": "สร้างพนักงาน",
    "permission.staff.edit": "แก้ไขพนักงาน",
    "permission.staff.delete": "ลบพนักงาน",
    "permission.settings.view": "ดูการตั้งค่า",
    "permission.settings.edit": "แก้ไขการตั้งค่า",
    "permission.roles.view": "ดูบทบาท",
    "permission.roles.create": "สร้างบทบาท",
    "permission.roles.edit": "แก้ไขบทบาท",
    "permission.roles.delete": "ลบบทบาท",

    // System Role Names
    "role.Super Admin": "ซูเปอร์แอดมิน",
    "role.Admin Manager": "ผู้จัดการแอดมิน",
    "role.Engine Manager": "ผู้จัดการเอนจิ้น",
    "role.Reseller Manager": "ผู้จัดการตัวแทนจำหน่าย",
    "role.Merchant Manager": "ผู้จัดการร้านค้า",
    "role.Transaction Viewer": "ผู้ดูธุรกรรม",
    "role.Staff Manager": "ผู้จัดการพนักงาน",
    "role.Viewer": "ผู้ดู",

    // System Role Descriptions
    "roleDesc.Full access to all admin functions": "เข้าถึงฟังก์ชันแอดมินทั้งหมด",
    "roleDesc.Manage admin operations": "จัดการการดำเนินงานแอดมิน",
    "roleDesc.Manage engines and their merchants": "จัดการเอนจิ้นและร้านค้าของพวกเขา",
    "roleDesc.Manage resellers and their merchants": "จัดการตัวแทนจำหน่ายและร้านค้าของพวกเขา",
    "roleDesc.Manage merchants": "จัดการร้านค้า",
    "roleDesc.View transactions only": "ดูธุรกรรมเท่านั้น",
    "roleDesc.Manage staff members": "จัดการสมาชิกพนักงาน",
    "roleDesc.Read-only access": "การเข้าถึงแบบอ่านอย่างเดียว",

    // Admin Staff Form
    editStaffMember: "แก้ไขสมาชิกพนักงาน",
    updateStaffMemberDetails: "อัปเดตรายละเอียดสมาชิกพนักงาน",
    addNewStaffMemberToOrganization: "เพิ่มสมาชิกพนักงานใหม่ในองค์กรของคุณ",
    basicDetailsForStaffMember: "รายละเอียดพื้นฐานสำหรับสมาชิกพนักงาน",
    fullName: "ชื่อเต็ม",
    roleAssignment: "การกำหนดบทบาท",
    assignRolesToControlAccess: "กำหนดบทบาทเพื่อควบคุมสิ่งที่สมาชิกพนักงานนี้สามารถเข้าถึงได้",
    noRolesAvailableCreateFirst: "ไม่มีบทบาทที่ใช้ได้ กรุณาสร้างบทบาทก่อน",
    updateStaffMember: "อัปเดตสมาชิกพนักงาน",
    loadingUserInformation: "กำลังโหลดข้อมูลผู้ใช้...",
    unableToGetCurrentUserId: "ไม่สามารถรับ ID ผู้ใช้ปัจจุบันได้ กรุณารีเฟรชหน้าและลองอีกครั้ง",
    pleaseProvidePassword: "กรุณาให้รหัสผ่านสำหรับสมาชิกพนักงานใหม่",
    passwordMustBeAtLeast6Characters: "รหัสผ่านต้องมีความยาวอย่างน้อย 6 ตัวอักษร",

    // Login Forms
    pleaseEnterBothEmailAndPassword: "กรุณากรอกอีเมลและรหัสผ่าน",
    loginFailed: "เข้าสู่ระบบล้มเหลว กรุณาตรวจสอบข้อมูลประจำตัวของคุณ",
    loginFailedCheckCredentials: "เข้าสู่ระบบล้มเหลว กรุณาตรวจสอบข้อมูลประจำตัวและประเภทแผงควบคุม",
    pleaseFillInAllFields: "กรุณากรอกข้อมูลในทุกช่อง",
    welcomeBack: "ยินดีต้อนรับกลับ",

    // Admin Reseller
    editReseller: "แก้ไขตัวแทนจำหน่าย",
    generatePassword: "สร้างรหัสผ่าน",

    providerTransactionId: "รหัสธุรกรรมของผู้ให้บริการ",
    systemId: "รหัสระบบ",
    processorBankcode: "รหัสธนาคาร",
    payoutType: "ประเภทการจ่าย",
    platform: "แพลตฟอร์ม",
    ipAddress: "ที่อยู่ IP",
    latestResponse: "การตอบกลับล่าสุด",
    copiedToClipboard: "คัดลอกไปยังคลิปบอร์ดแล้ว",
    passwordGeneratedAndCopied: "สร้างรหัสผ่านและคัดลอกไปยังคลิปบอร์ดแล้ว",
    apiKeysRegenerated: "สร้างคีย์ API ใหม่สำเร็จแล้ว",
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("language") || "en";
    }
    return "en";
  });

  useEffect(() => {
    localStorage.setItem("language", language);
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === "en" ? "th" : "en");
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    return {
      language: "en",
      setLanguage: () => { },
      toggleLanguage: () => { },
      t: (key) => translations.en?.[key] || key,
    };
  }
  return context;
}

export default LanguageContext;